/* ============================================
   QUOTE WORKSTATION — Freight & Installation
   Asset: workstation-freight.js
   Freight panel, installation panel, vendor rows, carriers, markup,
   target distribution, staleness check, email modal system
   ============================================ */
(function() {
  'use strict';
  var Q = window.QWS;
  if (!Q) return;
  var state = Q.state;
  var dom = Q.dom;
  var wrap = Q.wrap;
  var API = Q.API;
  var isAdmin = Q.isAdmin;
  var fmt = Q.fmt;
  var parseMoney = Q.parseMoney;
  var escHtml = Q.escHtml;
  var escAttr = Q.escAttr;
  var showToast = Q.showToast;
  var syncCustomSelect = Q.syncCustomSelect;

  /* CUSTOM_CARRIERNAMES_BY_CLAUDE (ONYX 609f1d1514ca, Ryan 2026-10-16) - one 'FedEx Priority' read as a freight truck AND an
     overnight parcel; it is three choices now. Keep in step with dashboard-freight-cards.js and the worker's LINE_HAUL_NAMES. */
  var CARRIER_PRESETS = ['FedEx Freight', 'FedEx Freight Priority', 'FedEx Freight Economy', 'FedEx Ground', 'FedEx Priority Overnight', 'ABF Freight', 'XPO Logistics', 'Estes Express', 'Old Dominion', 'SAIA', 'R+L Carriers', 'Daylight Transport', 'YRC Freight', 'Forward Air', 'Go Rocket Shipping', 'Other'];

  /* ── Delivery-condition wording (mirrors src/lib/delivery-conditions.js on
     the worker — keep in sync). Reps choose three flags; the derived liftgate
     sentence is the load-bearing part of every outbound document. */
  function pickDeliveryFlags(dc) {
    var d = dc || {};
    return {
      insideDelivery: !!d.insideDelivery,
      loadingDock: !!d.loadingDock,
      forkLift: !!d.forkLift,
      ftl: d.ftl === true, // CUSTOM_FTLFLAG_BY_CLAUDE - mirrors src/lib/delivery-conditions.js
    };
  }
  function formatLiftgateLine(dc) {
    var f = pickDeliveryFlags(dc);
    if (f.ftl) return 'Full truckload (26 or 53 ft trailer): no liftgate. The customer needs a loading dock or a forklift.'; // CUSTOM_FTLFLAG_BY_CLAUDE
    if (f.loadingDock && f.forkLift) return 'Liftgate not required — customer has both a loading dock and a forklift.';
    if (f.loadingDock) return 'Liftgate not required — customer has a loading dock.';
    if (f.forkLift) return 'Liftgate not required — customer has a forklift.';
    return 'Liftgate required — curbside delivery (no loading dock, no forklift).';
  }
  function formatLiftgateShort(dc) {
    var f = pickDeliveryFlags(dc);
    if (f.ftl) return 'Full truckload (no liftgate)'; // CUSTOM_FTLFLAG_BY_CLAUDE
    if (f.loadingDock && f.forkLift) return 'No liftgate — loading dock + forklift';
    if (f.loadingDock) return 'No liftgate — loading dock';
    if (f.forkLift) return 'No liftgate — forklift';
    return 'Liftgate required (curbside)';
  }
  function formatDeliveryLines(dc) {
    var f = pickDeliveryFlags(dc);
    var lines = [formatLiftgateLine(f)];
    if (f.insideDelivery) lines.push('Inside delivery required.');
    return lines;
  }
  /* CUSTOM_RFQPORTAL_BY_CLAUDE — ADD ONS lines: only ACTIVE delivery
     requirements (things that add to the shipment cost / complexity).
     Liftgate is only an add-on when there's no loading dock AND no forklift
     at the destination. Inside delivery is always an add-on when checked.
     Suppliers can assume defaults when an item isn't listed — no need to
     surface "not required" prose anymore. */
  function buildAddOnsLines(dc, vendorRow) {
    var f = pickDeliveryFlags(dc);
    var lines = [];
    /* CUSTOM_FTLFLAG_BY_CLAUDE (Ryan 2026-10-22, JW-1316-A) - a full truckload has no liftgate and no inside delivery: the ask says
       truckload instead. Mirrors src/lib/ftl-flag.js on the worker. */
    if (vendorRow && isFtlRow(vendorRow, dc)) return ['Full truckload (26 or 53 ft trailer), no liftgate'];
    if (!f.loadingDock && !f.forkLift) lines.push('Liftgate required');
    if (f.insideDelivery) lines.push('Inside delivery required');
    return lines;
  }
  /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
  /* CUSTOM_FTLFLAG_BY_CLAUDE (Ryan 2026-10-22, JW-1316-A) - FULL TRUCKLOAD, per supplier like Parcel. A row's own `ftl` wins; with
     none, the rep's whole-quote ask (deliveryConditions.ftl, the Request Quote dropdown) counts. A row shipping with another
     supplier rides that one. Mirrors src/lib/ftl-flag.js; keep the two in step. */
  function isFtlRow(v, dc) {
    if (!v) return false;
    if (v.ftl === true) return true;
    if (v.ftl === false) return false;
    return !!(dc && dc.ftl === true);
  }
  function shippingRows() {
    return ((state.freight && state.freight.vendors) || []).filter(function(v) { return v && v.name && !v.linkedTo; });
  }
  function ftlRows() {
    var dc = (state.freight && state.freight.deliveryConditions) || {};
    return shippingRows().filter(function(v) { return isFtlRow(v, dc); });
  }
  function allFtl() {
    var ship = shippingRows();
    return ship.length > 0 && ftlRows().length === ship.length;
  }
  /* Ryan 2026-10-22: "When the FTL box is checked, should the Curbside/Driveway box auto un-check?" Yes, once EVERY supplier is a
     truckload (curbside means a liftgate); it ticks back when no supplier is one. A mixed quote leaves it as it is. Same rule as
     the worker's POST /freight-ftl-set. */
  function syncCurbsideToFtl() {
    var dc = state.freight && state.freight.deliveryConditions;
    if (!dc) return;
    if (allFtl()) dc.defaultDelivery = false;
    else if (shippingRows().length > 0 && ftlRows().length === 0) dc.defaultDelivery = true;
  }
  /* END CUSTOM_FTLFLAG_BY_CLAUDE */

  var freightPanel = wrap.querySelector('.rq-ws-freight-panel');
  var installPanel = wrap.querySelector('.rq-ws-install-panel');

  /* Hold-charged target tracker. When admin focuses a vendor's Cost cell,
     we snapshot the current markedUpTotal here. After the cost changes,
     a "Hold $X" button appears next to the Charged cell — clicking it
     restores markedUpTotal to the snapshot and back-computes markup so
     the customer-facing price stays put. Module-local map, never persisted
     to KV — the button is only meaningful during the current edit session.
     Keyed by vendor name (which is stable across re-renders). */
  var freightHoldTargets = {};

  if (isAdmin && freightPanel) {
    freightPanel.style.display = '';
    var shipRow = wrap.querySelector('[data-fee-row="shipping"]');
    if (shipRow) shipRow.classList.add('rq-ws-fees__row--no-border');
  }
  if (isAdmin && installPanel) {
    installPanel.style.display = '';
    var instRow = wrap.querySelector('[data-fee-row="installation"]');
    if (instRow) instRow.classList.add('rq-ws-fees__row--no-border');
  }

  var vendorContacts = {};
  if (isAdmin) {
    API.get('/vendor-contacts').then(function(data) {
      if (data && data.contacts) vendorContacts = data.contacts;
    }).catch(function() {});
  }

  /* ── Spin toggle helper (local copy) ── */
  function spinToggle(icon, newSymbol) {
    icon.style.animation = 'none';
    icon.offsetHeight;
    icon.style.animation = 'rq-spin-once 0.4s ease';
    setTimeout(function() { icon.textContent = newSymbol; }, 200);
  }

  /* Toggle expand/collapse for detail panels */
  [freightPanel, installPanel].forEach(function(panel) {
    if (!panel) return;
    var toggle = panel.querySelector('[class$="__toggle"]');
    if (!toggle) return;
    toggle.addEventListener('click', function() {
      var body = this.nextElementSibling;
      var icon = this.querySelector('[class$="__toggle-icon"]');
      var isOpen = body.classList.contains(body.className.split(' ')[0] + '--open');
      var openClass = body.className.split(' ')[0] + '--open';
      if (!isOpen) {
        if (panel === freightPanel) renderFreightVendors();
        if (panel === installPanel) renderInstallCompanies();
      }
      if (isOpen) {
        body.style.overflow = 'hidden';
        body.style.maxHeight = body.scrollHeight + 'px';
        body.offsetHeight;
        body.style.maxHeight = '0';
        body.classList.remove(openClass);
      } else {
        body.style.overflow = 'hidden';
        body.classList.add(openClass);
        var h = body.scrollHeight;
        body.style.maxHeight = '0px';
        body.offsetHeight;
        body.style.maxHeight = h + 'px';
        var b = body;
        var onEnd = function() {
          b.style.maxHeight = 'none';
          b.style.overflow = 'visible';
          b.removeEventListener('transitionend', onEnd);
        };
        body.addEventListener('transitionend', onEnd);
      }
      if (icon) spinToggle(icon, isOpen ? '+' : '−');
    });
  });

  /* ── Freight Vendor Rendering ── */
  // Internal-only canonical names (matches src/lib/vendor-canonical.js on the
  // worker). Shopify product `vendor` fields stay raw for SEO; collapse the
  // moment data crosses into the workstation. Defensive for pre-backfill quotes.
  var VENDOR_ALIASES = { 'ecore athletic': 'ECORE' };
  function canonicalVendor(name) {
    if (!name) return name;
    var k = String(name).trim().toLowerCase();
    return VENDOR_ALIASES[k] || String(name).trim();
  }

  function getUniqueVendors() {
    var map = {};
    var order = [];
    state.items.forEach(function(item) {
      var v = canonicalVendor(item.vendor || 'Other');
      if (!map[v]) { map[v] = []; order.push(v); }
      map[v].push(item);
    });
    return { map: map, order: order };
  }

  /* ── CUSTOM_FREIGHT_BY_CLAUDE (Ryan 2026-08-13) — what the shipment actually
     presents to a carrier ─────────────────────────────────────────────────────

     Every freight dollar in this business is still a human quote: a rep emails a
     vendor or broker and types the number back in. Nothing here changes that.
     What this strip does is stop the rep guessing at the inputs — how many
     handling units, how heavy, what class, parcel or LTL or a truck — when
     rfq-worker already knows, from the vendor's own published skid data plus
     every shipment we have since measured.

     Read-only by design. It sits under the vendor row, next to the Cost box the
     rep is about to type into, and it cannot change a quote.

     ⚠ NO ARITHMETIC HAPPENS HERE. Class, mode and billable weight all come from
     /freight-quote-inputs. The density ladder lives once, in the worker's
     lib/freight-profile.js, so that a quote and a bill of lading can never
     disagree — and a copy in the browser would be exactly that second copy. */

  /* Keyed by vendor name -> { sig, state: 'loading'|'ok'|'error'|'none', data }.
     `sig` is the item signature the result was fetched for, so editing a
     quantity refetches and simply re-rendering does not. */
  var freightProfileCache = {};

  /* Every line this vendor's shipment carries, including vendors linked into it
     via "Ships with" — mirrors aggregateItemsForVendor, which is what the
     freight email already sends, so the strip describes the same shipment the
     rep is asking a broker to price. */
  function freightProfileLines(vendorName) {
    var vInfo = getUniqueVendors();
    var names = [vendorName];
    state.freight.vendors.forEach(function(ov) {
      if (ov.linkedTo === vendorName) names.push(ov.name);
    });
    var lines = [];
    names.forEach(function(n) {
      (vInfo.map[n] || []).forEach(function(item) {
        if (item.excludeFromFreight) return;
        if (!item.productHandle) return;
        lines.push({
          handle: item.productHandle,
          sku: item.sku || null,
          qty: item.quantity || 1,
          title: item.title || '',
        });
      });
    });
    return lines;
  }

  function freightProfileSig(lines) {
    return lines.map(function(l) { return l.handle + 'x' + l.qty; }).join('|');
  }

  function loadFreightProfile(vendorName, onDone) {
    var lines = freightProfileLines(vendorName);
    var sig = freightProfileSig(lines);
    var cached = freightProfileCache[vendorName];
    if (cached && cached.sig === sig) { onDone(cached); return; }

    if (!lines.length) {
      freightProfileCache[vendorName] = { sig: sig, state: 'none', data: null };
      onDone(freightProfileCache[vendorName]);
      return;
    }

    var entry = { sig: sig, state: 'loading', data: null };
    freightProfileCache[vendorName] = entry;
    onDone(entry);

    API.post('/freight-quote-inputs', {
      ref: state.ref || undefined,
      lines: lines.map(function(l) { return { handle: l.handle, sku: l.sku, qty: l.qty }; }),
    }).then(function(res) {
      /* Only land a result that still matches what is on screen — a rep who
         changed a quantity mid-flight must not be shown the old shipment. */
      if (freightProfileCache[vendorName] !== entry) return;
      entry.state = 'ok';
      entry.data = res;
      entry.titles = {};
      entry.vendorName = vendorName;
      lines.forEach(function(l) { entry.titles[l.handle] = l.title; });
      onDone(entry);
    }).catch(function(err) {
      if (freightProfileCache[vendorName] !== entry) return;
      /* "We could not load it" and "there is nothing to show" are different
         facts and a rep has to be able to tell them apart — an error rendered
         as an empty strip reads as "this shipment needs no freight". */
      entry.state = 'error';
      entry.error = (err && err.message) || 'unavailable';
      onDone(entry);
    });
  }

  function fmtLbs(n) {
    if (!isFinite(n)) return '—';
    return Math.round(n).toLocaleString() + ' lb';
  }

  var MODE_LABEL = { parcel: 'Parcel', ltl: 'LTL', ftl: 'Truckload' };

  /* Plain English about how much to trust the numbers. `provenance` is per line,
     so the WEAKEST line governs — a shipment is only as measured as its least
     known piece.

     The buffer sentence is not optional politeness. The weight shown is BILLABLE,
     which for an unmeasured product is the vendor's figure plus a confidence
     buffer, and for a parcel piece is dimensional weight. Either way it is
     legitimately not the number on the spec sheet, and a rep who spots the
     difference with no explanation on screen will assume the tool is broken. */
  function freightProfileTrust(res) {
    var lines = (res && res.lines) || [];
    if (!lines.length) return null;
    var soft = null, anyMeasured = false, maxBuffer = 0;
    lines.forEach(function(l) {
      if (l.provenance === 'measured') anyMeasured = true;
      else if (l.provenance === 'disputed') soft = 'disputed';
      else if (l.provenance === 'estimate' || l.provenance === 'orphan-estimate') { if (soft !== 'disputed') soft = 'estimate'; }
      if (l.buffer > maxBuffer) maxBuffer = l.buffer;
    });

    var source;
    if (soft === 'disputed') source = 'One item has a disputed weight, seeded high on purpose until a real shipment settles it.';
    else if (soft === 'estimate') source = 'Partly estimated from how the vendor packs similar items.';
    else if (anyMeasured && !maxBuffer) source = 'Measured from real shipments.';
    else source = "From the vendor's own published figures.";

    /* Why billable is not the spec-sheet weight, and the reason differs by mode.
       On parcel the gap is dimensional weight (the carrier bills the box's size);
       on LTL it is the confidence buffer. Saying "buffer" over a parcel line
       would be a confident wrong answer to the question a rep is actually asking. */
    var ship = (res && res.shipment) || {};
    if (ship.mode === 'parcel' && ship.dimLbs > ship.actualLbs) {
      source += ' Billable is dimensional weight, so it runs above the scale weight.';
    } else if (maxBuffer > 0) {
      source += ' Billable includes a ' + Math.round(maxBuffer * 100) + '% buffer while unmeasured.';
    }
    return source;
  }

  /* Vendor name off the front, the way buildItemData already does it for the
     supplier email — "Legend Fitness 3144-P Custom Platform Logo" in a warning
     strip is mostly the word Legend. */
  function shortItemTitle(title, vendorName) {
    var t = String(title || '');
    if (!vendorName) return t;
    var vn = String(vendorName).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return t.replace(new RegExp('^' + vn + '\\s+', 'i'), '');
  }

  /* What to call a handling unit. A rep says "pallet"; the profile knows whether
     the piece is actually on one. Calling a 40 lb boxed bar jack a pallet on a
     screen a rep quotes freight from is a small lie that turns into a real one
     the moment they repeat it to a broker. */
  var NOUN = { skid: 'pallet', carton: 'carton', loose: 'piece' };

  function unitNoun(units, n) {
    var word = NOUN[(units && units[0] && units[0].kind) || 'skid'] || 'piece';
    return word + (n === 1 ? '' : 's');
  }

  /* "4 pallets + 1 piece", not "5 pieces".

     A jungle gym ships as four skids plus one long hand-freight piece that
     cannot go on a pallet. Flattening that to the weakest shared noun throws
     away the word the rep actually uses, and rounding it UP to "5 pallets"
     would be a small lie that becomes a real one the moment they repeat it to
     a broker. Counting each kind costs a few characters and is simply true. */
  function unitCountLabel(units) {
    var order = ['skid', 'carton', 'loose'];
    var counts = {};
    (units || []).forEach(function(u) {
      var k = u.kind || 'skid';
      counts[k] = (counts[k] || 0) + 1;
    });
    var parts = [];
    order.forEach(function(k) {
      if (counts[k]) parts.push('<strong>' + counts[k] + '</strong> ' + NOUN[k] + (counts[k] === 1 ? '' : 's'));
    });
    return parts.join(' + ');
  }

  function fmtDims(u) {
    return u.length + '"L x ' + u.width + '"W x ' + u.height + '"H';
  }

  /* Identical units collapse to one row with a count. A jungle gym that ships as
     two matching 69x56x94 skids should say "2 pallets", not print the same line
     twice and make a rep count them. */
  function groupUnits(units) {
    var out = [], index = {};
    (units || []).forEach(function(u) {
      var k = [u.kind, u.length, u.width, u.height, u.weightLbs, u.densityClass].join('|');
      if (index[k]) { index[k].n++; return; }
      index[k] = { n: 1, u: u };
      out.push(index[k]);
    });
    return out;
  }

  function freightProfileHtml(entry) {
    if (!entry || entry.state === 'none') return '';
    if (entry.state === 'loading') {
      return '<span class="rq-ws-freight-profile rq-ws-freight-profile--muted">Checking what this ships as…</span>';
    }
    if (entry.state === 'error') {
      return '<span class="rq-ws-freight-profile rq-ws-freight-profile--warn" title="' + escAttr(entry.error || '') + '">'
        + 'Could not load freight details. That is a loading failure, not an empty shipment</span>';
    }
    var res = entry.data || {};
    var ship = res.shipment || {};
    var lines = res.lines || [];
    var missing = res.missing || [];
    var out = '';

    if (ship.handlingUnits) {
      var allUnits = [];
      lines.forEach(function(l) { allUnits = allUnits.concat(l.units || []); });

      /* COLLAPSED: two facts, and nothing that can truncate. Everything else
         earns its place only once a rep asks for it. */
      out += '<button type="button" class="rq-ws-freight-profile rq-ws-freight-profile__toggle" '
        + 'data-freight-detail-toggle aria-expanded="false" '
        + 'title="Show the pieces this shipment presents to a carrier">'
        + '<span class="rq-ws-freight-profile__chevron" aria-hidden="true">▸</span>'
        + unitCountLabel(allUnits)
        + '<span class="rq-ws-freight-profile__dot">·</span>'
        + '<strong>' + fmtLbs(ship.billableLbs) + '</strong>'
        + '</button>';

      /* EXPANDED: a block per line item, a row per distinct piece inside it.
         Dimensions are the point — they are what a broker asks for and what
         nothing in this business had written down before. */
      var d = '<div class="rq-ws-freight-detail" hidden>';
      lines.forEach(function(l) {
        var title = shortItemTitle((entry.titles && entry.titles[l.key]) || l.title || '', entry.vendorName);
        d += '<div class="rq-ws-freight-detail__item">';
        d += '<div class="rq-ws-freight-detail__title">' + escHtml(title || l.key)
          + (l.qty > 1 ? ' <span class="rq-ws-freight-detail__qty">x' + l.qty + '</span>' : '') + '</div>';
        groupUnits(l.units).forEach(function(g) {
          var bits = [];
          bits.push('<strong>' + g.n + '</strong> ' + unitNoun([g.u], g.n));
          bits.push(fmtLbs(g.u.weightLbs) + (g.n > 1 ? ' ea' : ''));
          bits.push(fmtDims(g.u));
          if (g.u.densityClass) bits.push('class <strong>' + g.u.densityClass + '</strong>');
          d += '<div class="rq-ws-freight-detail__unit">'
            + bits.join('<span class="rq-ws-freight-profile__dot">·</span>') + '</div>';
        });
        d += '</div>';
      });

      /* The rollup a broker actually gets asked for, kept out of the collapsed
         row but not thrown away. */
      var foot = [];
      if (ship.mode) foot.push('<strong>' + (MODE_LABEL[ship.mode] || ship.mode) + '</strong>');
      if (ship.class && ship.class.density) foot.push('class <strong>' + ship.class.density + '</strong> overall');
      if (ship.linearFt) foot.push((Math.round(ship.linearFt * 10) / 10) + ' linear ft');
      if (ship.totalCuFt) foot.push(Math.round(ship.totalCuFt).toLocaleString() + ' cu ft');
      if (foot.length) {
        d += '<div class="rq-ws-freight-detail__foot">'
          + foot.join('<span class="rq-ws-freight-profile__dot">·</span>') + '</div>';
      }
      var trust = freightProfileTrust(res);
      if (trust) d += '<div class="rq-ws-freight-detail__trust">' + escHtml(trust) + '</div>';
      d += '</div>';
      out += d;
    }

    if (missing.length) {
      var names = missing.map(function(mi) {
        var t = (entry.titles && entry.titles[mi.key]) || '';
        return shortItemTitle(t, entry.vendorName) || mi.sku || mi.key || 'an item';
      });
      /* Stays in the COLLAPSED row on purpose. A rep must not have to open
         anything to find out part of this shipment cannot be quoted. */
      out += '<span class="rq-ws-freight-profile rq-ws-freight-profile--warn" title="'
        + escAttr(names.join(', ')) + '">⚠ no freight data for '
        + escHtml(names.length > 2 ? names.slice(0, 2).join(', ') + ' +' + (names.length - 2) + ' more' : names.join(', '))
        + '</span>';
    }
    return out;
  }

  /* Fill the strip in place rather than re-rendering the table — the rep may be
     mid-keystroke in the Cost box when the request comes back, and rebuilding
     the row under them would take the focus and the caret with it. */
  function paintFreightProfile(container, vi, vendorName) {
    /* Admin only, and the guard belongs HERE rather than at the render site.
       renderFreightVendors() runs for every rep — workstation-quotes.js and
       workstation-totals.js both call it — and the freight panel is simply
       display:none for non-admins. So without this, a rep who cannot see the
       panel still fires a request per vendor and can read the answer out of
       the network tab. Hidden is not the same as not sent. */
    if (!isAdmin) return;
    loadFreightProfile(vendorName, function(entry) {
      var host = container.querySelector('.rq-ws-freight-profile-host[data-freight-idx="' + vi + '"]');
      if (!host) return;
      host.innerHTML = freightProfileHtml(entry);

      /* Bound here, not in bindFreightEvents — this markup arrives after the
         request resolves, which is long after the table was bound. */
      var toggle = host.querySelector('[data-freight-detail-toggle]');
      var detail = host.querySelector('.rq-ws-freight-detail');
      if (!toggle || !detail) return;
      /* Remember it per vendor: a rep who opened the detail and then edits a
         quantity should not have it slam shut on the re-render. */
      if (entry.open) {
        detail.hidden = false;
        toggle.setAttribute('aria-expanded', 'true');
        toggle.classList.add('is-open');
      }
      toggle.addEventListener('click', function() {
        var open = detail.hidden;
        detail.hidden = !open;
        entry.open = open;
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        toggle.classList.toggle('is-open', open);
      });
    });
  }
  /* END CUSTOM_FREIGHT_BY_CLAUDE */

  function syncFreightVendors() {
    var vInfo = getUniqueVendors();
    // Index existing by canonical name so a stored "ECORE Athletic" row
    // re-attaches to the canonical "ECORE" key derived from items.
    var existing = {};
    state.freight.vendors.forEach(function(v) {
      var canon = canonicalVendor(v.name);
      v.name = canon;
      if (!existing[canon]) existing[canon] = v;
      // Drop dupes silently — first occurrence wins (cost overwrites would be lossy).
    });

    var updated = [];
    vInfo.order.forEach(function(name) {
      if (existing[name]) {
        updated.push(existing[name]);
      } else {
        // New vendors inherit the rep's destination conditions so admin's
        // freight panel pre-checks the right boxes without manual re-entry.
        var dc = (state.freight && state.freight.deliveryConditions) || {};
        updated.push({
          name: name, cost: 0, source: null, carrier: null,
          type: 'unset', markup: 10,
          markedUpTotal: 0, notes: '', liftgate: false, residential: false,
          insideDelivery: !!dc.insideDelivery, loadingDock: !!dc.loadingDock, forkLift: !!dc.forkLift,
          /* CUSTOM_WILLCALL_BY_CLAUDE (Ryan 2026-09-17) — false is the safe default: an unticked
             row promises the carrier call, which is what every row has always done. An existing
             row keeps whatever it was ticked to, because this branch only builds NEW ones. */
          insurance: false, callAhead: false, parcel: false, linkedTo: null,
          ftl: !!dc.ftl // CUSTOM_FTLFLAG_BY_CLAUDE - a new supplier inherits the rep's whole-quote truckload ask
        });
      }
    });
    var names = vInfo.order;
    updated.forEach(function(v) {
      if (v.linkedTo && names.indexOf(v.linkedTo) === -1) v.linkedTo = null;
    });
    state.freight.vendors = updated;
  }

  /* CUSTOM_SUPPLIERFEE_BY_CLAUDE (ONYX 41775fe3a0bb, Ryan 2026-10-16) - TWIN of rfq-worker lib/ws-logistics.js
     freightMarkupBase: a row's `supplierFee` (the supplier's pallet / handling fee, worked out by the worker from the
     deal's lines and Product Rules) goes into the price BEFORE the markup. price = (cost + fee) x (1 + markup%). The cost
     box stays the carrier's. Every cost -> price and price -> markup step below reads this. */
  function freightFeeOf(v) {
    var a = parseFloat(v && v.supplierFee && v.supplierFee.amount);
    return a > 0 ? Math.round(a * 100) / 100 : 0;
  }
  function freightBase(v) {
    var cost = parseFloat(v && v.cost) || 0;
    if (!(cost > 0) && !(v && v._costEntered === true)) return Math.round(cost * 100) / 100;
    return Math.round((cost + freightFeeOf(v)) * 100) / 100;
  }
  function supplierFeeLineHtml(v) {
    var fee = freightFeeOf(v);
    if (!(fee > 0)) return '';
    var f = v.supplierFee || {};
    var parts = (f.lines && f.lines.length)
      ? f.lines.map(function (l) { return l.qty + ' x ' + fmt(l.unit) + (l.label ? ' ' + l.label : ''); }).join(', ')
      : (f.count ? f.count + ' x ' + fmt(f.rate || fee / f.count) : '');
    return '<div class="rq-ws-freight-table__fee" title="The supplier charges this to get the order ready for our truck. It is added to the freight before the markup, so the price covers it.">+ '
      + fmt(fee) + ' supplier fee' + (parts ? ' (' + escHtml(parts) + ')' : '') + ', before markup</div>';
  }
  /* CUSTOM_FIXEDCOST_BY_CLAUDE - the last worked-out (automatic) fee seen per supplier, so emptying a typed box can put it
     back without a round trip. Never saved. */
  var autoFeeByVendor = {};
  function noteAutoFee(v) {
    if (v && v.name && v.supplierFee && v.supplierFee.auto !== false) autoFeeByVendor[v.name] = v.supplierFee;
  }
  function fixedCostCellHtml(v, vi) {
    noteAutoFee(v);
    var f = v.supplierFee;
    var a = f ? parseFloat(f.amount) : NaN;
    var typed = !!(f && f.auto === false);
    var val = (isFinite(a) && (a > 0 || typed)) ? fmt(a) : '';
    var parts = '';
    if (f && !typed) {
      parts = (f.lines && f.lines.length)
        ? f.lines.map(function (l) { return l.qty + ' x ' + fmt(l.unit) + (l.label ? ' ' + l.label : ''); }).join(', ')
        : (f.count ? f.count + ' x ' + fmt(f.rate || a / f.count) + (f.label ? ' ' + f.label : '') : (f.label || ''));
    }
    var title = 'Fixed cost: pallet / handling fees for the products on this quote'
      + (typed ? ' (typed in)' : (parts ? ' (' + parts + ')' : '')) + '. Added to the cost before the markup. '
      + 'Type a figure to replace it (0 = no fee this time); empty the box to use the supplier fee again.';
    return '<input type="text" value="' + val + '" placeholder="$0.00" data-freight-idx="' + vi + '" data-freight-field="fixed"'
      + ' title="' + escAttr(title) + '" aria-label="Fixed cost: pallet / handling fees">';
  }
  /* Ask the worker once per deal + supplier list; a row not yet on the quote, not held and never stamped takes the fee. */
  var supplierFeeAsked = {};
  function ensureSupplierFees() {
    if (!state.ref || !API || !API.get) return;
    var names = state.freight.vendors.filter(function (v) { return v && v.name && !v.linkedTo; }).map(function (v) { return v.name; });
    if (!names.length) return;
    var key = state.ref + '|' + names.join('|');
    if (supplierFeeAsked[key]) return;
    supplierFeeAsked[key] = true;
    var q = '/freight-supplier-fees?ref=' + encodeURIComponent(state.ref) + names.map(function (n) { return '&vendor=' + encodeURIComponent(n); }).join('');
    API.get(q).then(function (res) {
      if (!res || !res.read || !res.fees) return;
      var moved = false;
      state.freight.vendors.forEach(function (v) {
        if (!v || v.linkedTo || !Object.prototype.hasOwnProperty.call(res.fees, v.name)) return;
        if (Object.prototype.hasOwnProperty.call(v, 'supplierFee') || v._appliedSig || v._chargedOverride) return;
        v.supplierFee = res.fees[v.name];
        noteAutoFee(v); // CUSTOM_FIXEDCOST_BY_CLAUDE
        moved = true;
      });
      if (!moved) return;
      state.dirty = true;
      recalcFreightTotals();
      renderFreightVendors();
    }).catch(function () { /* the rows keep their price */ });
  }

  /* CUSTOM_FTLCAPTWIN_BY_CLAUDE (ONYX 14be5d09f7ae) - a full truckload is marked up as usual, but never charged more than
     $2,000 above cost. Mirrors ftlCapCharged + FTL_MARKUP_CAP_USD in src/lib/ftl-flag.js; keep the two in step. */
  var FTL_MARKUP_CAP_USD = 2000;
  function ftlCapCharged(charged, cost, ftl) {
    var ch = Number(charged);
    var c = Number(cost);
    if (!ftl || !(c > 0) || !isFinite(ch)) return ch;
    var cap = Math.round((c + FTL_MARKUP_CAP_USD) * 100) / 100;
    return ch > cap ? cap : ch;
  }

  function recalcFreightTotals() {
    var totalCost = 0, totalQuoted = 0;
    var dc = state.freight.deliveryConditions || {}; // CUSTOM_FTLCAPTWIN_BY_CLAUDE - the rep's whole-quote FTL ask
    state.freight.vendors.forEach(function(v) {
      if (v.linkedTo) { v.markedUpTotal = 0; return; }
      if (!v._chargedOverride) {
        var base = freightBase(v);
        var forward = Math.round(base * (1 + (v.markup || 0) / 100) * 100) / 100; // CUSTOM_SUPPLIERFEE_BY_CLAUDE
        /* CUSTOM_FTLCAPTWIN_BY_CLAUDE - as src/lib/ws-logistics.js recalcFreightTotals: capped before it is shown or saved, the
           markup back-solved so the row agrees with its price. Never on `_chargedOverride`: a typed price is the rep's own. */
        var capped = Math.round(ftlCapCharged(forward, parseFloat(v.cost) || 0, isFtlRow(v, dc)) * 100) / 100;
        if (capped !== forward) v.markup = base > 0 ? Math.round(((capped / base) - 1) * 100 * 10000) / 10000 : 0;
        v.markedUpTotal = capped;
      }
      totalCost += v.cost || 0;
      totalQuoted += v.markedUpTotal;
    });
    state.freight.totalCost = Math.round(totalCost * 100) / 100;
    state.freight.totalQuoted = Math.round(totalQuoted * 100) / 100;

    if (freightPanel) {
      var costEl = freightPanel.querySelector('.rq-ws-freight-panel__cost-val');
      var quotedEl = freightPanel.querySelector('.rq-ws-freight-panel__quoted-val');
      if (costEl) costEl.textContent = fmt(state.freight.totalCost);
      if (quotedEl) quotedEl.textContent = fmt(state.freight.totalQuoted);
      var costEl2 = freightPanel.querySelector('.rq-ws-freight-panel__cost-val2');
      var quotedEl2 = freightPanel.querySelector('.rq-ws-freight-panel__quoted-val2');
      if (costEl2) costEl2.textContent = fmt(state.freight.totalCost);
      if (quotedEl2) quotedEl2.textContent = fmt(state.freight.totalQuoted);
    }
  }

  /* ── Autocomplete Utility (carrier/installer history) ── */
  var AUTOCOMPLETE_KEYS = { carrier: 'rq_ws_carrier_history', installer: 'rq_ws_installer_history' };
  function getAutoHistory(key) {
    try { return JSON.parse(localStorage.getItem(AUTOCOMPLETE_KEYS[key]) || '[]'); }
    catch(e) { return []; }
  }
  function addAutoHistory(key, value) {
    if (!value || !value.trim()) return;
    value = value.trim();
    var list = getAutoHistory(key);
    list = list.filter(function(v) { return v.toLowerCase() !== value.toLowerCase(); });
    list.unshift(value);
    if (list.length > 20) list = list.slice(0, 20);
    try { localStorage.setItem(AUTOCOMPLETE_KEYS[key], JSON.stringify(list)); } catch(e) {}
  }
  function removeAutoHistory(key, value) {
    var list = getAutoHistory(key);
    list = list.filter(function(v) { return v !== value; });
    try { localStorage.setItem(AUTOCOMPLETE_KEYS[key], JSON.stringify(list)); } catch(e) {}
  }
  function showAutocomplete(input, key, onSelect, extraOptions) {
    hideAutocomplete(input);
    var val = (input.value || '').toLowerCase().trim();
    var history = getAutoHistory(key);
    if (extraOptions && extraOptions.length) {
      var histSet = {};
      history.forEach(function(h) { histSet[h] = true; });
      var merged = [];
      extraOptions.forEach(function(o) { if (!histSet[o]) merged.push(o); });
      history = merged.concat(history);
    }
    var matches = val
      ? history.filter(function(h) { return h.toLowerCase().indexOf(val) !== -1; }).slice(0, 10)
      : history.slice(0, 12);
    if (matches.length === 0) return;

    var dd = document.createElement('div');
    dd.className = 'rq-ws-autocomplete';
    matches.forEach(function(m) {
      var item = document.createElement('div');
      item.className = 'rq-ws-autocomplete__item';
      var label = document.createElement('span');
      label.textContent = m;
      label.style.flex = '1';
      item.appendChild(label);
      var xBtn = document.createElement('span');
      xBtn.className = 'rq-ws-autocomplete__remove';
      xBtn.textContent = '×';
      xBtn.addEventListener('mousedown', function(e) {
        e.preventDefault();
        e.stopPropagation();
        removeAutoHistory(key, m);
        item.remove();
        if (!dd.querySelector('.rq-ws-autocomplete__item')) dd.remove();
      });
      item.appendChild(xBtn);
      item.addEventListener('mousedown', function(e) {
        e.preventDefault();
        input.value = m;
        onSelect(m);
        dd.remove();
      });
      dd.appendChild(item);
    });
    var rect = input.getBoundingClientRect();
    dd.style.position = 'fixed';
    dd.style.top = (rect.bottom) + 'px';
    dd.style.left = rect.left + 'px';
    dd.style.width = Math.max(rect.width, 160) + 'px';
    dd.style.zIndex = '9999';
    wrap.appendChild(dd);
    dd._acInput = input;
  }
  function hideAutocomplete(input) {
    wrap.querySelectorAll('.rq-ws-autocomplete').forEach(function(dd) {
      if (!input || dd._acInput === input) dd.remove();
    });
  }
  // Pre-seed carriers from presets
  (function() {
    var existing = getAutoHistory('carrier');
    if (existing.length === 0) {
      try { localStorage.setItem(AUTOCOMPLETE_KEYS.carrier, JSON.stringify(CARRIER_PRESETS)); } catch(e) {}
    } else {
      var set = {};
      existing.forEach(function(c) { set[c] = true; });
      var added = false;
      CARRIER_PRESETS.forEach(function(p) { if (!set[p]) { existing.push(p); added = true; } });
      /* CUSTOM_CARRIERNAMES_BY_CLAUDE - the retired ambiguous preset leaves the saved list too. */
      var _ix = existing.indexOf('FedEx Priority');
      if (_ix !== -1) { existing.splice(_ix, 1); added = true; }
      if (added) try { localStorage.setItem(AUTOCOMPLETE_KEYS.carrier, JSON.stringify(existing)); } catch(e) {}
    }
  })();

  /* ── Target Total Distribution ── */
  function distributeFreightTarget(targetTotal) {
    var totalCost = 0;
    var activeVendors = state.freight.vendors.filter(function(v) { return !v.linkedTo && v.cost > 0; });
    activeVendors.forEach(function(v) { totalCost += v.cost; });
    if (totalCost === 0 || activeVendors.length === 0) return;

    activeVendors.forEach(function(v) {
      var weight = v.cost / totalCost;
      var vendorCharged = Math.round(targetTotal * weight * 100) / 100;
      v.markedUpTotal = vendorCharged;
      v._chargedOverride = true;
      v.markup = freightBase(v) > 0 ? Math.round(((vendorCharged / freightBase(v)) - 1) * 100 * 10000) / 10000 : 0; // CUSTOM_SUPPLIERFEE_BY_CLAUDE
    });
    state.dirty = true;
    recalcFreightTotals();
    renderFreightVendors();
  }

  function renderFreightVendors() {
    if (!freightPanel) return;
    syncFreightVendors();
    ensureSupplierFees(); // CUSTOM_SUPPLIERFEE_BY_CLAUDE
    if (state.freight.blanketMarkup) {
      state.freight.vendors.forEach(function(v) { v.markup = state.freight.blanketMarkupPct || 10; });
      state.freight.blanketMarkup = false;
    }
    var tbody = freightPanel.querySelector('.rq-ws-freight-table__body');
    if (!tbody) return;

    var html = '';
    state.freight.vendors.forEach(function(v, vi) {
      // Linked vendors are folded into the primary's row, not rendered as
      // their own row. The Unlink button lives under the primary's vendor
      // name.
      if (v.linkedTo) return;
      var otherVendors = state.freight.vendors.filter(function(ov) { return ov.name !== v.name; });
      var typeClass = v.type === 'official' ? 'official' : v.type === 'estimate' ? 'estimate' : 'unset';
      if (!v.type) v.type = 'unset';
      var typeLabel = v.type === 'official' ? '✓' : v.type === 'estimate' ? 'E' : '?';
      var typeTitle = v.type === 'official' ? 'Official quote (click to toggle)' : v.type === 'estimate' ? 'Estimate (click to toggle)' : 'Not set — click to mark as Estimate or Official';
      // Other vendors that ship with me — i.e., vendors whose linkedTo points
      // at this row's vendor. Used to render a combined name + per-link Unlink.
      var linkedFroms = state.freight.vendors.filter(function(ov) { return ov.linkedTo === v.name; });
      var nameDisplay;
      if (linkedFroms.length > 0) {
        var slash = ' <span class="rq-ws-freight-table__vendor-slash">/</span> ';
        nameDisplay = linkedFroms.map(function(lf) { return escHtml(lf.name); }).join(slash) + slash + escHtml(v.name);
      } else {
        nameDisplay = escHtml(v.name);
      }

      html += '<tr data-freight-idx="' + vi + '">';
      html += '<td><span class="rq-ws-freight-table__vendor-name">' + nameDisplay + '</span>'; // CUSTOM_FIXEDCOST_BY_CLAUDE - the fee is its own column now
      if (linkedFroms.length > 0) {
        html += '<div class="rq-ws-freight-table__unlink-row">';
        linkedFroms.forEach(function(lf) {
          var lfIdx = state.freight.vendors.indexOf(lf);
          var label = linkedFroms.length === 1 ? 'Unlink' : 'Unlink ' + lf.name;
          html += '<button type="button" class="rq-ws-freight-table__unlink-btn" data-freight-idx="' + lfIdx + '" title="Unlink ' + escAttr(lf.name) + ' from this shipment">' + escHtml(label) + '</button>';
        });
        html += '</div>';
      }
      html += '</td>';

      html += '<td><div class="rq-ws-cost-cell">';
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-20) — $0 freight cost is a
         legitimate value (some vendors charge nothing for shipping). Without
         tracking "has admin entered a cost?" separately from "is cost > 0",
         typing 0 would re-render as empty placeholder + Apply would refuse
         the vendor as not-yet-quoted. Use v._costEntered as the explicit
         "admin made a choice" flag; legacy vendors with v.cost > 0 are
         backwards-compat treated as entered. */
      var _freightCostEntered = v._costEntered || v.cost > 0;
      html += '<input type="text" value="' + (_freightCostEntered ? fmt(v.cost || 0) : '') + '" placeholder="$0.00" data-freight-idx="' + vi + '" data-freight-field="cost" class="rq-ws-cost--' + typeClass + '">';
      html += '<button type="button" class="rq-ws-type-toggle rq-ws-type-toggle--' + typeClass + '" data-freight-idx="' + vi + '" title="' + typeTitle + '">' + typeLabel + '</button>';
      html += '</div></td>';
      /* CUSTOM_FIXEDCOST_BY_CLAUDE (Ryan 2026-10-16) - the Fixed cost column, the Deal Dashboard freight card's twin: the
         supplier's pallet / handling fees for the products on the quote, or a figure typed over it ("no fee this time").
         price = (cost + fixed) x (1 + markup). Both screens edit the same row.supplierFee, so a change on either shows on
         the other. Empty = back to the worked-out fee. */
      html += '<td>' + fixedCostCellHtml(v, vi) + '</td>';
      html += '<td><input type="text" value="' + (v.markup != null ? v.markup : 10) + '" data-freight-idx="' + vi + '" data-freight-field="markup"></td>';
      html += '<td><input type="text" value="' + fmt(v.markedUpTotal) + '" data-freight-idx="' + vi + '" data-freight-field="charged"></td>';
      html += '<td style="position:relative;"><input type="text" value="' + escAttr(v.carrier || '') + '" placeholder="Carrier..." data-freight-idx="' + vi + '" data-freight-field="carrier"></td>';
      html += '<td><input type="text" value="' + escAttr(v.notes || '') + '" placeholder="Notes..." data-freight-idx="' + vi + '" data-freight-field="notes"></td>';
      html += '<td class="rq-ws-freight-table__actions">';
      html += '<button type="button" class="rq-ws-freight-actions__btn" data-freight-idx="' + vi + '" data-action="email-freight" title="Email freight request">✉ Freight</button>';
      html += '<button type="button" class="rq-ws-freight-actions__btn" data-freight-idx="' + vi + '" data-action="email-dims" title="Email weight/dims request">✉ Wt/Dims</button>';
      html += '</td>';
      html += '</tr>';

      // Hide "Ships with:" once this vendor is already serving as the primary
      // of a combined shipment — preventing weird states where the primary
      // tries to ship with someone else and orphans its linked vendors.
      var canShowShipsWith = linkedFroms.length === 0;
      html += '<tr class="rq-ws-freight-table__flags-row" data-freight-idx="' + vi + '">';
      // Hold button lives in the empty cell beneath the vendor name — that
      // cell otherwise had no content. Always rendered (hidden by default)
      // so syncHoldBtn() can toggle visibility without rebuilding the row.
      html += '<td class="rq-ws-freight-table__hold-cell"><button type="button" class="rq-ws-freight-table__hold-btn" data-freight-idx="' + vi + '" data-action="hold-charged" style="display:none;"></button></td>';
      html += '<td colspan="7"><div class="rq-ws-freight-table__flags">'; // CUSTOM_FIXEDCOST_BY_CLAUDE - one more column
      /* CUSTOM_WILLCALL_BY_CLAUDE (Ryan 2026-09-17) — ⛔ PER SUPPLIER, NOT PER ORDER, which is why
         it sits here and not with the delivery conditions in the footer. Every ETA email ends
         "The carrier should also call you to schedule", and on a box on a van that call never
         comes. A two-supplier order with one pallet and one parcel still gets the call, so the
         flag has to be able to disagree between rows — an order-level tick could not.

         ⛔ WHY A TICK AND NOT A GUESS. Measured over 515 live freight rows: `callAhead`, the field
         that looks like it already answers this, is ticked on TWO of them — a dead control.
         Carrier names are free text (249 "Go Rocket Shipping", 116 blank, 25 a FedEx service,
         40-odd the supplier's own name), so classifying them fails silently in the direction that
         promises a customer a call nobody will make. Unticked is the default and is exactly
         today's behaviour, so nothing regresses while these fill in. */
      html += '<label class="rq-ws-freight-table__parcel" title="Small parcel — ships on a van, so no carrier calls the customer to schedule. Leave unticked for anything on a pallet.">'
        + '<input type="checkbox" data-freight-idx="' + vi + '" data-freight-field="parcel"'
        + (v.parcel ? ' checked' : '') + '> Parcel</label>';
      /* CUSTOM_FTLFLAG_BY_CLAUDE (Ryan 2026-10-22) - per supplier when two or more ship; with one supplier the box sits with the
         order-level delivery boxes in the footer (Ryan: "with the other only row of boxes"). */
      if (!v.linkedTo && shippingRows().length > 1) {
        html += '<label class="rq-ws-freight-table__parcel" title="Full truckload (26 or 53 ft trailer): no liftgate. The customer needs a loading dock or a forklift.">'
          + '<input type="checkbox" data-freight-idx="' + vi + '" data-freight-field="ftl"'
          + (isFtlRow(v, (state.freight && state.freight.deliveryConditions) || {}) ? ' checked' : '') + '> FTL</label>';
      }
      // Delivery condition flags moved to the order-level footer row
      // (same line as Target/Distribute) — they're a per-order value, not
      // per-vendor. Single source of truth is state.freight.deliveryConditions.
      if (canShowShipsWith && otherVendors.length > 0) {
        // Offer any vendor that isn't itself linked away to someone else.
        // Primaries of existing combined shipments stay selectable so a third
        // (or fourth) vendor can join — selecting a primary appends this row
        // to that primary's group rather than starting a new pair.
        var shipsWithCandidates = otherVendors.filter(function(ov) {
          return !ov.linkedTo;
        });
        if (shipsWithCandidates.length > 0) {
          html += '<span class="rq-ws-freight-table__ships-with">Ships with: <select data-freight-idx="' + vi + '" data-freight-field="linkedTo">';
          html += '<option value="">Independent</option>';
          shipsWithCandidates.forEach(function(ov) {
            html += '<option value="' + escAttr(ov.name) + '">' + escHtml(ov.name) + '</option>';
          });
          html += '</select></span>';
        }
      }
      /* CUSTOM_FREIGHT_BY_CLAUDE — the shipment's carrier inputs, filled in
         asynchronously by paintFreightProfile below. Empty host so the row
         height does not jump when the answer arrives. */
      html += '<span class="rq-ws-freight-profile-host" data-freight-idx="' + vi + '"></span>';
      html += '</div></td></tr>';
    });

    tbody.innerHTML = html;
    recalcFreightTotals();
    bindFreightEvents(tbody);
    /* CUSTOM_FREIGHT_BY_CLAUDE — one call per primary vendor, cached on the
       item signature, so re-rendering the table costs nothing. */
    state.freight.vendors.forEach(function(v, vi) {
      if (v.linkedTo) return;
      paintFreightProfile(tbody, vi, v.name);
    });
    /* END CUSTOM_FREIGHT_BY_CLAUDE */
    var shipsSelects = tbody.querySelectorAll('[data-freight-field="linkedTo"]');
    if (shipsSelects.length > 1) {
      var maxW = 0;
      shipsSelects.forEach(function(s) { if (s.offsetWidth > maxW) maxW = s.offsetWidth; });
      if (maxW > 0) shipsSelects.forEach(function(s) { s.style.minWidth = maxW + 'px'; });
    }
  }

  function bindFreightEvents(container) {
    // Unlink button — clears v.linkedTo and re-renders so the row reverts to
    // the standard freight cells (cost / markup / charged / etc).
    container.querySelectorAll('.rq-ws-freight-table__unlink-btn').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var idx = parseInt(this.getAttribute('data-freight-idx'));
        var v = state.freight.vendors[idx];
        if (!v) return;
        v.linkedTo = null;
        state.dirty = true;
        renderFreightVendors();
      });
    });
    // Capture markedUpTotal snapshot when admin focuses a vendor's Cost cell.
    // Drives the "Hold $X" button — see freightHoldTargets above.
    container.querySelectorAll('[data-freight-field="cost"]').forEach(function(el) {
      el.addEventListener('focus', function() {
        var idx = parseInt(this.getAttribute('data-freight-idx'));
        var v = state.freight.vendors[idx];
        if (v && v.markedUpTotal > 0 && freightHoldTargets[v.name] == null) {
          freightHoldTargets[v.name] = v.markedUpTotal;
        }
      });
    });
    // Show/hide + relabel the Hold button without rebuilding the row HTML
    // (rebuilding would yank focus from the Cost input mid-typing).
    function syncHoldBtn(vi) {
      var v = state.freight.vendors[vi];
      if (!v) return;
      var btn = container.querySelector('button[data-freight-idx="' + vi + '"][data-action="hold-charged"]');
      if (!btn) return;
      var holdTarget = freightHoldTargets[v.name];
      var shouldShow = holdTarget != null && Math.abs(holdTarget - (v.markedUpTotal || 0)) > 0.005;
      if (shouldShow) {
        btn.textContent = '↺ Hold $' + fmt(holdTarget);
        btn.title = 'Restore charged amount to $' + fmt(holdTarget) + ' and adjust markup % to fit the new cost';
        btn.style.display = '';
      } else {
        btn.style.display = 'none';
      }
    }
    // Hold button: restore the snapshotted charged amount and back-compute
    // the markup % so the customer-facing price stays put after a cost change.
    container.querySelectorAll('[data-action="hold-charged"]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var idx = parseInt(this.getAttribute('data-freight-idx'));
        var v = state.freight.vendors[idx];
        if (!v) return;
        var target = freightHoldTargets[v.name];
        if (target == null) return;
        v.markedUpTotal = target;
        v.markup = freightBase(v) > 0 ? Math.round(((target / freightBase(v)) - 1) * 100 * 10000) / 10000 : 0; // CUSTOM_SUPPLIERFEE_BY_CLAUDE
        v._chargedOverride = true;
        delete freightHoldTargets[v.name];
        state.dirty = true;
        renderFreightVendors();
      });
    });

    container.querySelectorAll('[data-freight-field]').forEach(function(el) {
      var evtType = el.tagName === 'SELECT' ? 'change' : 'input';
      el.addEventListener(evtType, function() {
        var idx = parseInt(this.getAttribute('data-freight-idx'));
        var field = this.getAttribute('data-freight-field');
        var v = state.freight.vendors[idx];
        if (!v) return;

        /* CUSTOM_WILLCALL_BY_CLAUDE (Ryan 2026-09-17) — ⛔ A CHECKBOX'S `value` IS THE STRING "on",
           whether it is ticked or not; only `checked` says. Handled first so it cannot fall
           through to a branch that would parse "on" as money. */
        if (field === 'parcel') {
          v.parcel = !!this.checked;
          state.dirty = true;
          return;
        }
        /* CUSTOM_FTLFLAG_BY_CLAUDE - one supplier's truckload box. Every supplier a truckload = no inside delivery. */
        if (field === 'ftl') {
          v.ftl = !!this.checked;
          state.dirty = true;
          syncCurbsideToFtl(); // Ryan 2026-10-22
          if (v.ftl && allFtl() && state.freight.deliveryConditions && state.freight.deliveryConditions.insideDelivery) setDeliveryFlag('insideDelivery', false);
          else { renderDeliveryHighlight(); renderFreightDeliveryFlags(); }
          /* CUSTOM_FTLCAPTWIN_BY_CLAUDE (ONYX 14be5d09f7ae) - the box moves the $2,000 cap: re-price the row and show it. */
          recalcFreightTotals();
          var _ftlCh = container.querySelector('[data-freight-idx="' + idx + '"][data-freight-field="charged"]');
          var _ftlMk = container.querySelector('[data-freight-idx="' + idx + '"][data-freight-field="markup"]');
          if (_ftlCh) _ftlCh.value = fmt(v.markedUpTotal);
          if (_ftlMk && typeof v.markup === 'number' && isFinite(v.markup)) _ftlMk.value = v.markup;
          return;
        }
        /* CUSTOM_FIXEDCOST_BY_CLAUDE - a typed figure is the row's fee (kept on every later save, `auto: false`); an
           empty box puts the worked-out fee back. Like a cost edit, it re-prices on the markup. */
        if (field === 'fixed') {
          var _fxRaw = String(this.value || '').replace(/[$,\s]/g, '');
          if (_fxRaw === '') {
            if (autoFeeByVendor[v.name]) v.supplierFee = autoFeeByVendor[v.name];
            else delete v.supplierFee;
          } else {
            var _fx = parseMoney(this.value);
            v.supplierFee = { amount: (_fx > 0 ? Math.round(_fx * 100) / 100 : 0), count: 0, label: 'Fixed cost', rate: 0, auto: false };
          }
          v._chargedOverride = false;
          Q.liveFmtMoney(this);
        } else if (field === 'cost') {
          v.cost = parseMoney(this.value);
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-20) — Stamp the
             "admin made a choice" flag the moment they type in the cost
             cell (even if the value is "0" / "$0.00"). Render + Apply
             gates honor this flag so $0 freight ships through. */
          v._costEntered = true;
          v._chargedOverride = false;
          Q.liveFmtMoney(this);
        } else if (field === 'markup') {
          v.markup = Math.round(parseFloat(this.value) * 10000) / 10000 || 0;
          v._chargedOverride = false;
          // Explicit markup edit: admin chose a new % themselves, so the
          // hold-snapshot is no longer the desired anchor — drop it.
          delete freightHoldTargets[v.name];
        } else if (field === 'charged') {
          var charged = parseMoney(this.value);
          if (freightBase(v) > 0) { // CUSTOM_SUPPLIERFEE_BY_CLAUDE - the markup is on cost + the supplier's fee
            v.markup = Math.round(((charged / freightBase(v)) - 1) * 100 * 10000) / 10000;
          } else {
            v.markup = 0;
          }
          v.markedUpTotal = charged;
          v._chargedOverride = true;
          // Manual charged edit dismisses any pending hold-snapshot.
          delete freightHoldTargets[v.name];
          var mkInput = container.querySelector('[data-freight-idx="' + idx + '"][data-freight-field="markup"]');
          if (mkInput) mkInput.value = v.markup;
          state.dirty = true;
          recalcFreightTotals();
          return;
        } else if (field === 'linkedTo') {
          v.linkedTo = this.value || null;
          renderFreightVendors();
          return;
        } else if (field === 'carrier') {
          v.carrier = this.value;
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-20) — sync Apply button
             when admin picks a carrier from the autocomplete dropdown.
             showAutocomplete sets input.value programmatically (no 'input'
             event fires) + invokes onSelect, so without this the button
             stayed muted until admin clicked it. Mirrors the general-input
             sync at the bottom of this handler. */
          showAutocomplete(this, 'carrier', function(val) { v.carrier = val; state.dirty = true; syncFreightApplyBtn(); }, v.name ? [v.name] : []);
        } else {
          v[field] = this.value;
        }
        state.dirty = true;
        recalcFreightTotals();
        var chargedInput = container.querySelector('[data-freight-idx="' + idx + '"][data-freight-field="charged"]');
        if (chargedInput && field !== 'charged') chargedInput.value = fmt(v.markedUpTotal);
        /* CUSTOM_FTLCAPTWIN_BY_CLAUDE - a cost change that hit the FTL cap back-solved the markup; show it (never while the rep
           is typing in the markup box itself). */
        var markupInput = container.querySelector('[data-freight-idx="' + idx + '"][data-freight-field="markup"]');
        if (markupInput && field !== 'markup' && typeof v.markup === 'number' && isFinite(v.markup) && parseFloat(markupInput.value) !== v.markup) markupInput.value = v.markup;
        syncHoldBtn(idx);
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-20) — Re-sync the Apply
           button after any vendor field change. Was previously only called
           after type-toggle clicks (~L645) + initial render (~L677) + Apply
           success (~L785), so entering a cost on the last unset vendor left
           the button visually grayed even though the gate was now satisfied.
           Reverse case (admin clearing a cost so a vendor falls back to
           unset) also wasn't reflected. Cheap idempotent recompute. */
        syncFreightApplyBtn();
      });

      if (el.getAttribute('data-freight-field') === 'cost') {
        el.addEventListener('focusout', function() {
          var n = parseMoney(this.value);
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-20) — Preserve "$0.00"
             when admin explicitly typed a value (even 0). Was wiping back
             to placeholder on blur because `n ? ... : ''` treated 0 as
             falsy. Now defers to v._costEntered which the input handler
             stamps on any keystroke. */
          var _idx = parseInt(this.getAttribute('data-freight-idx'));
          var _v = state.freight.vendors[_idx];
          var _entered = _v && _v._costEntered;
          this.value = (_entered || n > 0) ? fmt(n) : '';
        });
      }
      if (el.getAttribute('data-freight-field') === 'charged') {
        el.addEventListener('focusout', function() {
          var n = parseMoney(this.value);
          this.value = n ? fmt(n) : '';
        });
      }
      if (el.getAttribute('data-freight-field') === 'carrier') {
        el.addEventListener('focus', function() {
          var idx = parseInt(this.getAttribute('data-freight-idx'));
          var v = state.freight.vendors[idx];
          var self = this;
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-20) — sync on autocomplete
             pick (focus-handler path). Same reason as the input-handler carrier
             branch above. */
          showAutocomplete(self, 'carrier', function(val) { if (v) { v.carrier = val; self.value = val; state.dirty = true; syncFreightApplyBtn(); } }, v && v.name ? [v.name] : []);
        });
      }
      if (el.getAttribute('data-freight-field') === 'carrier') {
        el.addEventListener('blur', function() {
          var self = this;
          setTimeout(function() {
            addAutoHistory('carrier', self.value);
            hideAutocomplete(self);
          }, 150);
        });
      }
    });

    container.querySelectorAll('[data-freight-flag]').forEach(function(cb) {
      cb.addEventListener('change', function() {
        var idx = parseInt(this.getAttribute('data-freight-idx'));
        var flag = this.getAttribute('data-freight-flag');
        var v = state.freight.vendors[idx];
        if (v) {
          v[flag] = this.checked;
          state.dirty = true;
          var label = this.parentElement;
          if (label) label.classList.toggle('rq-ws-freight-table__flag--active', this.checked);
        }
      });
    });

    container.querySelectorAll('.rq-ws-type-toggle').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var idx = parseInt(this.getAttribute('data-freight-idx'));
        var v = state.freight.vendors[idx];
        if (!v) return;
        /* CUSTOM_RFQPORTAL_BY_CLAUDE — Three-state cycle: unset → estimate
           → official → unset. Lets admin click their way back to ? if they
           bumped the button by accident, without needing to reload. */
        var next = { unset: 'estimate', estimate: 'official', official: 'unset' };
        v.type = next[v.type] || 'estimate';
        state.dirty = true;
        this.className = 'rq-ws-type-toggle rq-ws-type-toggle--' + v.type;
        this.textContent = v.type === 'official' ? '✓' : v.type === 'estimate' ? 'E' : '?';
        this.title = v.type === 'official'
          ? 'Official quote (click to toggle)'
          : v.type === 'estimate'
            ? 'Estimate (click to toggle)'
            : 'Not set — click to mark as Estimate or Official';
        /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
        var costInput = container.querySelector('[data-freight-idx="' + idx + '"][data-freight-field="cost"]');
        if (costInput) {
          costInput.classList.remove('rq-ws-cost--unset', 'rq-ws-cost--estimate', 'rq-ws-cost--official');
          costInput.classList.add('rq-ws-cost--' + v.type);
        }
        syncFreightApplyBtn();
      });
    });

    container.querySelectorAll('.rq-ws-freight-actions__btn').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var idx = parseInt(this.getAttribute('data-freight-idx'));
        var action = this.getAttribute('data-action');
        if (action === 'email-freight') {
          // Block if no zip on the relevant address — supplier can't quote
          // freight without it. Uses Ship To zip when "Ship To is different"
          // is checked, otherwise the Sold To zip.
          var c = Q.getCustomerData();
          var requiredZip = c.hasShipTo ? c.shipZip : c.zip;
          if (!(requiredZip && requiredZip.trim())) {
            showToast(c.hasShipTo
              ? 'Add a zip code to the Ship To section before requesting freight.'
              : 'Add a zip code to the Sold To section before requesting freight.', 'error');
            return;
          }
          openEmailModal(idx, 'freight');
        } else if (action === 'email-dims') openEmailModal(idx, 'dims');
      });
    });
    // Re-apply any active hold-charged buttons after a render. Hold targets
    // live in the closure-local map, but the row HTML was just rebuilt with
    // each Hold button hidden by default — sync them back into the right
    // visible state so admin's earlier "I want to keep $110" intent isn't
    // silently dropped on an unrelated re-render (e.g. carrier change).
    state.freight.vendors.forEach(function(v, vi) {
      if (freightHoldTargets[v.name] != null) syncHoldBtn(vi);
    });
    syncFreightApplyBtn();
  }

  /* Target total distribution button */
  if (freightPanel) {
    var targetInput = freightPanel.querySelector('.rq-ws-freight-panel__target-input');
    var targetBtn = freightPanel.querySelector('.rq-ws-freight-panel__target-apply');
    if (targetBtn && targetInput) {
      targetBtn.addEventListener('click', function() {
        var target = parseMoney(targetInput.value);
        if (target > 0) distributeFreightTarget(target);
      });
    }
  }

  /* Apply freight to quote */
  var freightApply = freightPanel ? freightPanel.querySelector('.rq-ws-freight-panel__apply') : null;
  /* CUSTOM_RFQPORTAL_BY_CLAUDE — Option B partial-freight workflow.
     Apply requires EVERY non-linked vendor to be fully quoted (type set
     AND cost > 0). While the admin is mid-multi-vendor build, save silently
     keeps everything as Pending on the customer-facing fee row; Apply +
     rep-notify only fires when all vendors are in.

     Button label reflects progress: "Apply (N of M set)" when partial,
     "Apply to Quote" when ready. Disabled state is visual + functional. */
  function freightVendorIsSet(v) {
    if (!v) return false;
    if (v.linkedTo) return true; // ships with another vendor — auto-resolved
    if (v.type === 'unset') return false;
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-20) — A vendor that charges
       us $0 for freight (some don't bill at all) is still "set" — admin made
       a real choice. Honor v._costEntered (stamped on any cost-cell input
       event) so $0 freight passes the Apply gate. Legacy vendors with
       v.cost > 0 stay set without the flag for backwards compat. */
    if (!(v._costEntered || parseFloat(v.cost) > 0)) return false;
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-20) — Carrier also mandatory.
       The PO render branches on carrier presence to print the "shipping
       via {Carrier}" line + flag whose carrier owns the shipment. Without
       it, the PO can't tell if this is our broker or the vendor's carrier.
       Even $0-freight vendors (vendor ships free via their own carrier)
       need it set so the PO denotes the right party. */
    if (!v.carrier || !String(v.carrier).trim()) return false;
    return true;
  }
  function freightApplyCounts() {
    var standalone = state.freight.vendors.filter(function (v) { return !v.linkedTo; });
    var setCount = standalone.filter(freightVendorIsSet).length;
    var totalCount = standalone.length;
    return { setCount: setCount, totalCount: totalCount };
  }
  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-20) — Snapshot helper for
     the "Freight Applied ✓" confirmed state. Serializes the fields that
     affect what gets pushed to the customer-facing quote (cost / markup /
     type / carrier / linkedTo / markedUpTotal). If the current snapshot
     matches state.freight._appliedSnapshot (captured at Apply time),
     nothing has changed since Apply — show "Freight Applied ✓". Any
     mutation → snapshot differs → button reverts to "Apply X to Quote". */
  /* CUSTOM_DDFREIGHTCARD_BY_CLAUDE (Ryan 2026-08-29) — PER-ROW APPLIED SIGNATURE.

     ⛔ THE DEAL DASHBOARD'S FREIGHT CARD READS THIS, AND A QW APPLY THAT DID NOT WRITE IT WOULD
     MAKE THE TWO SURFACES DISAGREE. The DD counts a row as "on the customer's quote" only while
     `_appliedSig` matches the row's current values (src/lib/ws-logistics.js: vendorIsApplied), so
     without these two writers an apply done HERE would leave the dashboard showing 0 of 4 applied
     on a deal whose freight is fully on the quote.

     ⛔ THE FIELD LIST MUST MATCH `vendorAppliedSignature` / `companyAppliedSignature` IN
     src/lib/ws-logistics.js EXACTLY, key order included — this is a JSON string compare, so a
     reordered key is a permanent mismatch and every row would read as edited-since-applied. Two
     implementations of one rule is a drift risk taken deliberately: the worker module is ESM on the
     server and cannot be imported here. tests/ws-logistics.test.mjs parses both files and fails if
     the two field lists diverge.

     📌 Deliberately NOT the same thing as `state.freight._appliedSnapshot` above, which answers
     "has ANYTHING changed since the last apply" for this button. This answers it per row, which is
     what a partial apply needs. */
  function _rowAppliedSig(v) {
    return JSON.stringify({
      cost: _sigNum(v.cost),
      markup: parseFloat(v.markup) || 0,
      markedUpTotal: _sigNum(v.markedUpTotal),
      type: String(v.type || 'unset'),
      carrier: String(v.carrier || '').trim(),
      linkedTo: v.linkedTo || null,
      costEntered: !!v._costEntered
    });
  }
  function _companyAppliedSig(c) {
    return JSON.stringify({
      name: String(c.name || '').trim(),
      cost: _sigNum(c.cost),
      markup: parseFloat(c.markup) || 0,
      markedUpTotal: _sigNum(c.markedUpTotal),
      type: String(c.type || 'unset')
    });
  }
  /* Mirrors round2 in the worker module. A bare parseFloat would let 924 and 924.0000001 differ. */
  function _sigNum(n) {
    var v = parseFloat(n);
    return isFinite(v) ? Math.round(v * 100) / 100 : 0;
  }
  function _stampAppliedVendors() {
    (state.freight.vendors || []).forEach(function (v) {
      if (!freightVendorIsSet(v)) return;
      v._appliedAt = new Date().toISOString();
      v._appliedSig = _rowAppliedSig(v);
    });
  }
  function _stampAppliedCompanies() {
    (state.installation.companies || []).forEach(function (c) {
      if (!c || !c.type || c.type === 'unset') return;
      c._appliedAt = new Date().toISOString();
      c._appliedSig = _companyAppliedSig(c);
    });
  }

  function freightApplySnapshot() {
    var vendors = (state.freight && state.freight.vendors) || [];
    return JSON.stringify(vendors.map(function (v) {
      return {
        name: v.name || '',
        cost: v.cost || 0,
        markup: v.markup || 0,
        markedUpTotal: v.markedUpTotal || 0,
        type: v.type || 'unset',
        carrier: v.carrier || '',
        linkedTo: v.linkedTo || null,
        _costEntered: !!v._costEntered,
      };
    }));
  }
  /* CUSTOM_QWAPPLIEDSIG_BY_CLAUDE (Ryan 2026-10-11, RW-1494-A/-B) — ⛔ THE ROW SIGNATURES FIRST.
     `_appliedSnapshot` is written only by THIS button. An apply from the Deal Dashboard, an agent
     or the auto-apply (/logistics-apply) signs each row (`_appliedSig`) and never touches it, so a
     quote whose freight was already on the customer's quote opened here reading "Apply to Quote"
     and invited a second apply. Same rule as the worker's `vendorIsApplied` (src/lib/ws-logistics.js):
     a signed row is applied while its signature still matches; an unsigned row falls back to the
     block's appliedAt. The fee check catches the one thing per-row cannot see, a row deleted
     since the apply. The snapshot stays only for records applied before rows were signed. */
  function freightIsAppliedAndClean() {
    if (!state.freight) return false;
    var vendors = state.freight.vendors || [];
    var signed = vendors.some(function (v) { return v && v._appliedSig; });
    if (!signed) {
      if (!state.freight._appliedSnapshot) return false;
      return freightApplySnapshot() === state.freight._appliedSnapshot;
    }
    var allApplied = vendors.every(function (v) {
      if (!freightVendorIsSet(v)) return false;
      if (v._appliedSig) return v._appliedSig === _rowAppliedSig(v);
      return !!state.freight.appliedAt;
    });
    if (!allApplied) return false;
    var fee = state.fees && state.fees[0];
    /* the Shipping row must carry it too: a Save As copies the signed rows onto a version whose Shipping row is Pending
       (RW-1494-B after it was set back to Pending), and that freight is not on its quote */
    if (!fee || (fee.status !== 'value' && fee.status !== 'free')) return false;
    if (fee.status === 'value' && Math.abs((parseFloat(fee.amount) || 0) - (state.freight.totalQuoted || 0)) >= 0.01) return false;
    return true;
  }
  /* END CUSTOM_QWAPPLIEDSIG_BY_CLAUDE */
  /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

  function syncFreightApplyBtn() {
    if (!freightApply) return;
    var c = freightApplyCounts();
    var ready = c.totalCount > 0 && c.setCount === c.totalCount;
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-20) — Show "Freight Applied
       ✓" when current state matches the apply-time snapshot. Click stays
       interactive (no hard disable) so admin can hover to read the title
       tip explaining "edit a row to re-apply". Cursor:default + the
       distinct label communicate that clicking is a no-op. */
    if (freightIsAppliedAndClean()) {
      freightApply.disabled = false;
      freightApply.setAttribute('aria-disabled', 'true');
      freightApply.style.opacity = '';
      freightApply.style.cursor = 'default';
      freightApply.textContent = 'Freight Applied ✓';
      freightApply.title = 'Freight totals are on the quote. Edit any vendor row to re-apply.';
      return;
    }
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    /* CUSTOM_RFQPORTAL_BY_CLAUDE — Not-ready state is muted-but-clickable.
       Clicking while not-ready fires the gate toast in the click handler
       (informs admin which vendors are missing). aria-disabled keeps assistive
       tech consistent; opacity + cursor signal "not yet" without locking
       the click out entirely. Full disabled was too strict — admin couldn't
       even probe what the button was waiting for. */
    freightApply.disabled = false;
    freightApply.setAttribute('aria-disabled', ready ? 'false' : 'true');
    if (ready) {
      freightApply.style.opacity = '';
      freightApply.style.cursor = '';
    } else {
      freightApply.style.opacity = '0.55';
      freightApply.style.cursor = 'not-allowed';
    }
    if (c.totalCount > 1 && !ready) {
      freightApply.textContent = 'Apply (' + c.setCount + ' of ' + c.totalCount + ')';
      freightApply.title = 'Set each remaining vendor to Estimate (E) or Official (✓) with a freight cost + carrier. Customer-facing quote stays Pending until every vendor is in. Click anyway to see what\'s missing.';
    } else if (c.totalCount > 1) {
      freightApply.textContent = 'Apply ' + c.totalCount + ' to Quote';
      freightApply.title = 'Apply final freight totals to the customer-facing quote and notify the rep.';
    } else {
      freightApply.textContent = 'Apply to Quote';
      freightApply.title = 'Apply final freight totals to the customer-facing quote and notify the rep.';
    }
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
  }
  if (freightApply) {
    freightApply.addEventListener('click', function() {
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-20) — No-op when the
         button is in its "Freight Applied ✓" confirmed state — admin
         already pushed these totals to the quote; clicking again would
         redundantly re-push. The button remains interactive so admins
         on touch can still hover/focus for the tip. */
      if (freightIsAppliedAndClean()) return;
      var c = freightApplyCounts();
      if (c.totalCount === 0 || c.setCount !== c.totalCount) {
        var missing = c.totalCount - c.setCount;
        showToast('Set ' + missing + ' more vendor' + (missing === 1 ? '' : 's') + ' first — ' + c.setCount + ' of ' + c.totalCount + ' quoted. Each needs type (E or ✓) + cost + carrier.', 'error');
        return;
      }
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-20) — pre-apply snapshot for
         rollback. If the save that now follows fails, we restore these so the
         button flips back to "Apply to Quote" instead of sitting on a green
         "Freight Applied ✓" that no longer describes anything on the server. */
      var _preApply = {
        freight: JSON.parse(JSON.stringify(state.freight)),
        fee: JSON.parse(JSON.stringify(state.fees[0])),
        dirty: state.dirty,
      };
      recalcFreightTotals();
      if (state.freight.vendors.length > 0) {
        state.freight.history.push({
          version: state.freight.history.length + 1,
          vendors: JSON.parse(JSON.stringify(state.freight.vendors)),
          totalCost: state.freight.totalCost,
          totalQuoted: state.freight.totalQuoted,
          date: new Date().toISOString()
        });
      }
      state.fees[0].amount = state.freight.totalQuoted;
      state.fees[0].status = 'value';
      state.freight.status = 'quoted';
      state.freight.appliedAt = new Date().toISOString();
      _stampAppliedVendors();
      state.freight.itemsSnapshot = buildItemsSnapshot();
      var shipInput = wrap.querySelector('.rq-ws-fees__input[data-fee="shipping"]');
      var shipSel = wrap.querySelector('.rq-ws-fees__status[data-fee="shipping"]');
      if (shipInput) { shipInput.value = fmt(state.freight.totalQuoted); shipInput.style.display = ''; }
      if (shipSel) { shipSel.value = 'value'; syncCustomSelect(shipSel); }
      state.dirty = true;
      Q.recalcTotals();
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-20) — Persistent "Freight
         Applied ✓" state. Snapshot the freight vendors at apply time;
         syncFreightApplyBtn compares current state against this snapshot
         on every render. If equal → "Freight Applied ✓"; if different →
         "Apply X to Quote". Auto-handles every mutation path (cost
         input, markup, type toggle, unlink, hold, charged) without
         hooking each. The 2-second flash-and-revert was dropped — admin
         needs a stable visual receipt, not a transient confirmation. */
      state.freight._appliedSnapshot = freightApplySnapshot();
      var btn = this;
      btn.style.background = '';
      btn.style.color = '';
      btn.style.pointerEvents = '';
      syncFreightApplyBtn();

      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-20) — SAVE, THEN notify.
         Apply used to mutate local state and fire the rep email immediately,
         calling it "best-effort — the local Apply already succeeded." But the
         local apply is not a durable fact: freight only reaches the server on
         a Save. On JW-1234-B the admin applied $452.14, the rep got "freight
         has been applied," and the deal still read freight.status='requested'
         with $0 — the numbers lived only in a browser tab.

         Now the same save path the Save button uses runs first (mirroring the
         freight-REQUEST flow in workstation-actions.js), and the notify fires
         only once that save resolves. A failed save leaves the rep un-emailed
         rather than lied to; saveDraft has already shown the failure toast. */
      if (state.ref) {
        var _saveBtn = wrap.querySelector('.rq-ws-send__save');
        var _applied = { totalAmount: state.freight.totalQuoted, vendorCount: c.totalCount };
        var _savePromise = (_saveBtn && Q.saveDraft)
          ? Q.saveDraft(_saveBtn)
          : Promise.resolve({ saved: false, reason: 'no-save-button' });
        _savePromise.then(function (res) {
          if (!res || !res.saved) {
            // Roll the apply back so the button stops claiming success.
            state.freight = _preApply.freight;
            // Mutate the fee in place — other modules hold a reference to
            // state.fees[0]; swapping the object would orphan them.
            state.fees[0].amount = _preApply.fee.amount;
            state.fees[0].status = _preApply.fee.status;
            state.dirty = _preApply.dirty;
            var rbInput = wrap.querySelector('.rq-ws-fees__input[data-fee="shipping"]');
            var rbSel = wrap.querySelector('.rq-ws-fees__status[data-fee="shipping"]');
            if (rbInput) rbInput.value = fmt(state.fees[0].amount || 0);
            if (rbSel) { rbSel.value = state.fees[0].status || 'pending'; syncCustomSelect(rbSel); }
            Q.recalcTotals();
            syncFreightApplyBtn();
            if (res && res.reason !== 'validation') {
              showToast('Freight NOT applied — the save failed. Fix the error and click Apply again.', 'error');
            }
            return;
          }
          return API.post('/freight-ready-notify', {
            ref: state.ref,
            totalAmount: _applied.totalAmount,
            vendorCount: _applied.vendorCount,
          }).catch(function () { /* non-fatal — freight IS saved either way */ });
        });
      }
    });
  }
  /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

  function buildItemsSnapshot() {
    var vInfo = getUniqueVendors();
    var snap = [];
    vInfo.order.forEach(function(vendor) {
      var items = vInfo.map[vendor];
      var parts = items.map(function(i) { return (i.sku || i.title) + '×' + i.quantity; }).sort();
      snap.push(vendor + ':' + parts.join(','));
    });
    return snap;
  }

  /* ── Stale Freight Detection ── */
  var _lastItemsChangeKey = '';
  function checkFreightStaleness() {
    /* CUSTOM_QWALERTS_BY_CLAUDE (Ryan 2026-10-23, Pass 2) - this notice lives in the page's alerts banner now
       (workstation-actions.js `itemsChangedAlert`, which every rep sees); the panel's own box stays hidden. */
    if (Q.renderAlerts) {
      var _moved = freightPanel && freightPanel.querySelector('.rq-ws-freight-panel__stale');
      if (_moved) _moved.style.display = 'none';
      Q.renderAlerts();
      /* CUSTOM_ITEMSCHANGEDREQUEST_BY_CLAUDE (Ryan 2026-10-25) - the Fees & Extras Re-Request follows the items as they are
         edited. Only when the answer changes: a repaint closes an open Request drop-down, and this runs on every recalc. */
      var _ch = Q.freightItemsChange ? Q.freightItemsChange() : null;
      var _key = _ch ? _ch.sig : '';
      if (_key !== _lastItemsChangeKey) { _lastItemsChangeKey = _key; if (Q.updateRequestButton) Q.updateRequestButton(); }
      return;
    }
    if (!freightPanel) return;
    var banner = freightPanel.querySelector('.rq-ws-freight-panel__stale');
    if (!banner) return;

    if (state.freight.status !== 'quoted' || !state.freight.itemsSnapshot || !state.freight.itemsSnapshot.length) {
      banner.style.display = 'none';
      return;
    }

    var currentSnap = buildItemsSnapshot();
    var oldSnap = state.freight.itemsSnapshot;

    if (JSON.stringify(currentSnap) === JSON.stringify(oldSnap)) {
      banner.style.display = 'none';
      return;
    }

    var oldMap = {};
    oldSnap.forEach(function(s) {
      var idx = s.indexOf(':');
      if (idx !== -1) oldMap[s.substring(0, idx)] = s.substring(idx + 1);
    });
    var newMap = {};
    currentSnap.forEach(function(s) {
      var idx = s.indexOf(':');
      if (idx !== -1) newMap[s.substring(0, idx)] = s.substring(idx + 1);
    });

    var changes = [];
    Object.keys(newMap).forEach(function(v) {
      if (!oldMap[v]) changes.push('<span style="color:var(--rq-success);">+ New vendor: ' + escHtml(v) + '</span>');
    });
    Object.keys(oldMap).forEach(function(v) {
      if (!newMap[v]) changes.push('<span style="color:var(--rq-error);">− Removed vendor: ' + escHtml(v) + '</span>');
    });
    Object.keys(newMap).forEach(function(v) {
      if (oldMap[v] && oldMap[v] !== newMap[v]) {
        changes.push('<span style="color:#d97706;">↔ Items changed: ' + escHtml(v) + '</span>');
      }
    });

    if (changes.length === 0) {
      banner.style.display = 'none';
      return;
    }

    banner.innerHTML = '<strong>⚠ Items changed since freight was quoted</strong><br>' +
      changes.join('<br>') +
      '<br><button class="rq-ws-freight-panel__rerequest" type="button" style="margin-top:6px;padding:4px 12px;border:1px solid #d97706;border-radius:4px;background:transparent;color:#d97706;font-size:11px;font-weight:600;cursor:pointer;">Re-request Freight Quote</button>';
    banner.style.display = '';

    var reReq = banner.querySelector('.rq-ws-freight-panel__rerequest');
    if (reReq) {
      reReq.addEventListener('click', function() {
        state.freight.status = 'pending';
        state.freight.requestedAt = null;
        state.freight.requestedBy = null;
        state.dirty = true;
        if (Q.updateFeeDropdownOptions) Q.updateFeeDropdownOptions();
        banner.style.display = 'none';
        /* CUSTOM_QWALERTS_BY_CLAUDE (Ryan 2026-10-23: "I'm not convinced the current re-request button does") - it did
           not: it reset the status on screen and requested nothing until somebody also used Request Quote and saved.
           Now it presses that same Submit, so the zip check, the stamp, the save and the agent wake all happen. */
        if (Q.requestFreightNow) Q.requestFreightNow();
      });
    }
  }

  /* ── Freight / Dims email builders ── */
  var ROLL_DIM_RE = /^\(\d+x?\)\s*rolls?\s*@/i;

  /* CUSTOM_RFQPORTAL_BY_CLAUDE — Sub-description auto-inclusion rules for the
     supplier freight/dims email body. Each rule fires when ALL three matchers
     pass:
       vendorMatch  — RegExp tested against the vendor name. Use a permissive
                      pattern like dot-star to match any vendor.
       titleMatch   — fn(title) → bool; lets rules require keyword combos like
                      "must contain ≥2 of [custom, inlaid, platform, platforms]".
       subDescMatch — RegExp tested against the sub-description text.

     Add new rules by appending to this array. Manual per-subdesc toggles in
     the email preview modal can override a rule's verdict for one-off cases.

     Current rules:
       1. Roll-quantity-with-@ pattern — universal.
       2. ECORE custom/inlaid platforms — auto-include dimension subtext
          (4' x 4', 6x8, 5*8, etc.) so the supplier can quote freight without
          a back-and-forth. */
  var SUBDESC_RULES = [
    {
      name: 'roll-quantity',
      vendorMatch: /.*/,
      titleMatch: function () { return true; },
      subDescMatch: ROLL_DIM_RE,
    },
    {
      name: 'ecore-custom-platform-dims',
      vendorMatch: /ecore/i,
      titleMatch: function (t) {
        var lt = String(t || '').toLowerCase();
        var kws = ['custom', 'inlaid', 'platform', 'platforms'];
        var hits = 0;
        for (var i = 0; i < kws.length; i++) {
          if (lt.indexOf(kws[i]) !== -1) hits++;
        }
        return hits >= 2;
      },
      // Dimension shapes: 4' x 4', 4'x4', 6x8, 6 * 8, 5×8, 5 × 8'
      // Optional foot/inch marks + flexible separator (x, ×, *) with optional
      // whitespace. Anchored loosely so longer subdesc lines that contain a
      // dim pattern still match (e.g. "Custom Size: 4' x 4'").
      subDescMatch: /\d+\s*['"’]?\s*[x×*]\s*\d+/i,
    },
  ];

  // Test a sub-description against all SUBDESC_RULES with the item's vendor +
  // title context. Returns true on first matching rule. Used by buildItemData
  // to pre-check the auto-included state in the email preview modal.
  function subDescAutoIncluded(text, vendor, title) {
    if (!text) return false;
    for (var i = 0; i < SUBDESC_RULES.length; i++) {
      var r = SUBDESC_RULES[i];
      if (r.vendorMatch && !r.vendorMatch.test(vendor || '')) continue;
      if (typeof r.titleMatch === 'function' && !r.titleMatch(title || '')) continue;
      if (r.subDescMatch && r.subDescMatch.test(text)) return true;
    }
    return false;
  }
  /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
  /* CUSTOM_FREIGHTTYPE_BY_CLAUDE (Ryan 2026-08-27) — delegates to the shared rule in
     custom-rep-portal.js. This was the SECOND copy of an exact-match list that read the rep's
     "N/a - Residence" as a company name and told the supplier a house was commercial; the Deal
     Dashboard's freight modal had the identical bug. Two answers to "is this a house" on one deal
     is how the Workstation and the Dashboard email a supplier different things about one address.
     Full reasoning, and why this must NOT borrow the worker's GENERIC_ORG_TOKENS, lives on
     destinationTypeFromOrg. Fallback direction explained there too. */
  function classifyDestination(org) {
    return (window.RepPortal && window.RepPortal.destinationTypeFromOrg)
      ? window.RepPortal.destinationTypeFromOrg(org)
      : 'Residence';
  }
  /* END CUSTOM_FREIGHTTYPE_BY_CLAUDE */

  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-22) — isEcoreVendor() removed. It
     hardcoded a "Hi West Team," greeting for ECORE in the freight/dims builders,
     which meant the same vendor got greeted differently from QW than from the
     Quote Dashboard. Greetings now live in the vendor's PO Profile body where
     they're editable without a deploy. */

  function detectUOM(item) {
    /* CUSTOM_RFQPORTAL_BY_CLAUDE — Connectors / accessories are counted by the
       piece, never by tile/sqft/roll, even when their NAME contains a flooring
       word for compatibility (e.g. 'Quad Blok Connector for 1" Tiles'). Without
       this guard the substring match below picks up "tiles" and the freight
       email qty reads "(Nx) tiles" instead of just "(Nx)". */
    if (/\bconnector\b/i.test(item.title || '')) return null;
    var text = ((item.title || '') + ' ' + (item.subDescriptions || []).map(function(s) { return s.value || s; }).join(' ')).toLowerCase();
    if (text.indexOf('sq ft') !== -1 || text.indexOf('square feet') !== -1 || text.indexOf('square foot') !== -1) return 'sq ft';
    if (text.indexOf('tiles') !== -1 || text.indexOf('tile') !== -1) return 'tiles';
    if (text.indexOf('rolls') !== -1 || text.indexOf('roll') !== -1) return 'rolls';
    if (text.indexOf('pavers') !== -1 || text.indexOf('paver') !== -1) return 'pavers';
    return null;
  }

  function buildItemData(vendorName) {
    var vInfo = getUniqueVendors();
    // Filter out items the rep flagged as in-house / marketing — they appear
    // on the customer's quote but not in the supplier's freight email.
    var items = (vInfo.map[vendorName] || []).filter(function(it) { return !it.excludeFromFreight; });
    var rows = [];
    items.forEach(function(item) {
      var qty = item.quantity || 1;
      var title = item.title || '';
      var sku = item.sku || '';
      if (vendorName) {
        var vn = vendorName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        title = title.replace(new RegExp('^' + vn + '\\s+', 'i'), '');
      }
      var uom = detectUOM(item);
      var qtyStr = uom ? '(' + qty + 'x) ' + uom : '(' + qty + 'x)';
      // CUSTOM_RFQPORTAL_BY_CLAUDE — Build BOTH the auto-included subLines
      // (what actually goes into the supplier email by default, via the
      // SUBDESC_RULES engine) AND the full subDescChoices list (every
      // sub-description on the item with an `included` flag). Modal preview
      // renders the choices as toggleable chips so admin can manually flip
      // a subdesc in/out per item before send.
      var subLines = [];
      var subDescChoices = [];
      if (item.subDescriptions && item.subDescriptions.length) {
        item.subDescriptions.forEach(function(sd) {
          var txt = sd.value || sd || '';
          if (!txt) return;
          var included = subDescAutoIncluded(txt, vendorName, item.title || '');
          subDescChoices.push({ text: txt, included: included });
          if (included) subLines.push(txt);
        });
      }
      rows.push({ qtyStr: qtyStr, title: title, sku: sku, subLines: subLines, subDescChoices: subDescChoices });
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    });
    return rows;
  }

  // Aggregate items for a freight email: the primary vendor's items, plus any
  // other vendor whose `linkedTo` points at the primary (i.e. "Ships with X").
  // Each row is tagged with its source vendor so the table can render a small
  // badge per row when the shipment is multi-vendor.
  function aggregateItemsForVendor(primaryName) {
    var primary = buildItemData(primaryName).map(function(r) {
      r._vendor = primaryName;
      return r;
    });
    var linkedRows = [];
    state.freight.vendors.forEach(function(ov) {
      if (ov.linkedTo === primaryName) {
        buildItemData(ov.name).forEach(function(r) {
          r._vendor = ov.name;
          linkedRows.push(r);
        });
      }
    });
    return primary.concat(linkedRows);
  }

  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-22) — Freight-email consolidation.
     The Workstation now composes supplier freight emails exactly like the Quote
     Dashboard does: per-vendor verbiage pulled from the Vendor Guide PO Profile,
     the {…_BLOCK} token vocabulary, width-less <table> cards, and the Internal-ref
     footer. Previously QW hardcoded its own copy and ignored the profile entirely,
     so the two surfaces sent visibly different emails for the same deal. */

  // Per-vendor freight verbiage (Edit Vendor → PO Profile). Cached per vendor for
  // the life of the page. Non-fatal: an empty object means "use the built-ins".
  var vendorFreightCache = {};
  function fetchVendorFreight(vendorName) {
    if (!vendorName) return Promise.resolve({});
    if (vendorFreightCache[vendorName]) return Promise.resolve(vendorFreightCache[vendorName]);
    /* CUSTOM_ONEFREIGHTCOMPOSER_BY_CLAUDE (Ryan 2026-08-28) — send the deal ref when there IS one.
       This call used to deliberately omit it ("the Workstation has no deal yet"), which was true of
       a brand-new quote and false of every saved one. It matters more now than it did: the response
       carries the vendor's "Emails Send From" rule, and on a white-label row the name on the line is
       OURS — without a ref the worker can only resolve an alias with a single claimant, so a shared
       one answers for nobody and the freight ask goes out from the wrong mailbox. Unsaved quote →
       the lifecycle read misses and it falls back exactly as before. */
    var _q = '/vendor-freight-email?vendor=' + encodeURIComponent(vendorName)
      + (state.ref ? '&ref=' + encodeURIComponent(state.ref) : '');
    return API.get(_q)
      .then(function (fv) { vendorFreightCache[vendorName] = fv || {}; return vendorFreightCache[vendorName]; })
      .catch(function () { vendorFreightCache[vendorName] = {}; return vendorFreightCache[vendorName]; });
  }

  /* CUSTOM_ONEFREIGHTCOMPOSER_BY_CLAUDE (Ryan 2026-08-28) — shortenVendorName, renderFreightVars
     and FREIGHT_DEFAULTS were deleted from this file. They were a SECOND copy of the gold-standard
     freight/dims copy and the {VENDOR}/{ZIP}/{REF} substitution, carrying a "keep in sync with
     dashboard-freight-modal.js" comment — which is a bug waiting on someone editing one and not
     the other. The composer owns them now, and it is the only thing that renders a subject or a
     body, so there is nothing left here to keep in sync. */


  /* CUSTOM_ONEFREIGHTCOMPOSER_BY_CLAUDE (Ryan 2026-08-28) — ONE payload builder for both asks.
     It used to be buildFreightContent + buildDimsContent, near-identical twins that each also
     rendered the subject, the body copy, the items HTML and a plain-text dump. All four of those
     now belong to dashboard-freight-modal.js, which is the composer both surfaces open — so this
     is reduced to the only thing the Workstation actually knows and the Deal Dashboard does not:
     WHICH ITEMS, at what quantities, going where.

     ⛔ A dims ask still gets shipToParts and addOnsLines. They are not dropped here — the composer
     suppresses both for `type === 'dims'` (one rule, one place). Deciding it twice is how the two
     surfaces drifted in the first place. */
  function buildFreightPayload(vendorIdx) {
    var v = state.freight.vendors[vendorIdx];
    if (!v) return null;
    var customer = Q.getCustomerData();
    // Ship To when "Ship To is different" is ticked, else Sold To. The supplier cares where the
    // goods physically go, not where the bill lands.
    var useShip = !!customer.hasShipTo;
    var zip = ((useShip ? customer.shipZip : customer.zip) || '').trim();
    var city = ((useShip ? customer.shipCity : customer.city) || '').trim();
    var st = ((useShip ? customer.shipState : customer.state) || '').trim();
    var org = ((useShip ? customer.shipCompany : customer.company) || '').trim();
    var destType = classifyDestination(org);

    /* ADD ONS lines — rendered by the composer as their own card via {ADD_ONS_BLOCK} when any
       delivery requirement is active; the token collapses to nothing when none, so the supplier
       infers the defaults. Services-only vendors skip it: no goods, no add-ons. */
    var addOnsLines = [];
    if (!/install|freight|shipping/i.test(v.name || '')) {
      addOnsLines = buildAddOnsLines((state.freight && state.freight.deliveryConditions) || v, v) || []; // CUSTOM_FTLFLAG_BY_CLAUDE - the row, for its truckload box
    }

    return {
      vendorName: v.name,
      zip: zip,
      rows: aggregateItemsForVendor(v.name),
      shipToParts: { location: (city && st ? city + ', ' + st + ' ' + zip : (city || '') + ' ' + zip).trim(), type: destType },
      addOnsLines: addOnsLines,
    };
  }

  /* ── Freight / weight-dims request — sent state ── */
  var sentEmails = {};

  function markSentButton(vendorIdx, type) {
    var action = type === 'freight' ? 'email-freight' : 'email-dims';
    var btn = wrap.querySelector('.rq-ws-freight-actions__btn[data-freight-idx="' + vendorIdx + '"][data-action="' + action + '"]');
    if (!btn) return;
    btn.textContent = type === 'freight' ? '✓ Freight Sent' : '✓ Wt/Dims Sent';
    btn.style.background = '#1a7f37';
    btn.style.color = '#fff';
    btn.style.borderColor = '#1a7f37';
  }

  function fetchEmailSentLog(ref) {
    if (!ref) return;
    API.get('/email-sent-log?ref=' + encodeURIComponent(ref)).then(function(data) {
      if (!data || !data.log) return;
      sentEmails = {};
      var vInfo = getUniqueVendors();
      Object.keys(data.log).forEach(function(key) {
        var parts = key.split(':');
        var vendorName = parts.slice(0, -1).join(':');
        var type = parts[parts.length - 1];
        var idx = vInfo.order.indexOf(vendorName);
        if (idx === -1) return;
        var sentKey = idx + '-' + type;
        sentEmails[sentKey] = true;
        markSentButton(idx, type);
      });
    }).catch(function() {});
  }

  /* CUSTOM_ONEFREIGHTCOMPOSER_BY_CLAUDE (Ryan 2026-08-28) — the Workstation's freight/weight-dims
     window is GONE; this is now an adapter onto the shared composer. Ryan, comparing the two
     screens: *"I want them both to use the DD one... less code that way."*

     What was deleted with it: a second body editor, a second **bold** parser, a second items-table
     renderer, a second signature, a second HTML composer, and two more copies of the send + test
     calls — every one of which was free to disagree with the Deal Dashboard's for the same deal.
     Also gone, deliberately: "Copy to clipboard". Ryan's call — the composer has no equivalent and
     the plain-text dump only existed to feed it.

     ⚠️ The vendor verbiage fetch stays HERE, ahead of the open, because the composer takes the
     profile as data (`vendorFreight`) rather than fetching it — that same object now also carries
     the vendor's "Emails Send From" rule, so a slow Guide would otherwise mean a freight ask from
     the wrong mailbox rather than merely default copy. */
  function openEmailModal(vendorIdx, type) {
    var payload = buildFreightPayload(vendorIdx);
    if (!payload) return;
    if (!window.DashboardFreightModal || typeof window.DashboardFreightModal.open !== 'function') {
      showToast('Composer not loaded yet — refresh the page.', 'error');
      return;
    }
    var kind = type === 'dims' ? 'dims' : 'freight';
    fetchVendorFreight(payload.vendorName).then(function (vf) {
      window.DashboardFreightModal.open({
        quoteRef: state.ref || '',
        vendorName: payload.vendorName,
        type: kind,
        zip: payload.zip,
        items: payload.rows,
        shipToParts: payload.shipToParts,
        addOnsLines: payload.addOnsLines,
        vendorFreight: vf || {},
        // Remembered per-vendor contacts; the Guide's freight overrides beat them inside the
        // composer (mergeVendorRecipients), so this is the fallback layer only.
        vendorRecipients: vendorContacts[payload.vendorName] || {},
        onSent: function (info) {
          sentEmails[vendorIdx + '-' + kind] = true;
          markSentButton(vendorIdx, kind);
          showToast(info && info.scheduledFor
            ? 'Held for business hours — sending automatically at the next window.'
            : 'Request sent to ' + payload.vendorName, 'success');
        },
      });
    });
  }


  /* ── Installation Panel ── */

  /* One shape, one place. The placeholder the panel draws, the row "+ Add Installer" appends and
     the row `ensureInstallRow` commits have to be identical, or they diverge the first time one
     of them gains a field. */
  function blankInstaller() {
    return { name: '', cost: 0, type: 'unset', markup: 10, markedUpTotal: 0, notes: '' };
  }

  /* CUSTOM_DDINSTALL_BY_CLAUDE (Ryan 2026-09-03) — ⛔ THE EMPTY ROW IS A VIEW, NOT STATE.
     ─────────────────────────────────────────────────────────────────────────────
     `renderInstallCompanies` used to PUSH a blank company into
     `state.installation.companies` whenever the list was empty, purely so the table had a row to
     draw. `buildSavePayload` sends `installation: state.installation` verbatim, so that
     view-only row was persisted on the very next save — and workstation-quotes.js calls this
     renderer on EVERY quote load for an admin, not on panel open. So opening any deal as an
     admin and saving planted an installer nobody added.

     ⛔ WHICH IS EXACTLY WHAT THE DEAL DASHBOARD THEN DREW. Its Installation card renders one row
     per `wsInstallation.companies` entry and its header counts `companies.length`, so the deal
     came back reading "INSTALLATION (0/1)" with an empty installer row — indistinguishable from
     somebody having pressed "+ Add installer". Ryan, 2026-09-03: *"the only thing that should be
     able to click this button is either me or Elon."*

     The fix is that a renderer must not write to persisted state. The placeholder is drawn from a
     throwaway object; the row is committed to `companies` only when somebody actually edits it. */
  function ensureInstallRow(idx) {
    var list = state.installation.companies;
    if (list[idx]) return list[idx];
    /* Only ever materialises the ONE placeholder the renderer just drew — never a gap, and never
       past the two-installer ceiling. Anything else means the DOM and the state disagree, and
       inventing a row to paper over that would hide the real fault. */
    if (idx !== list.length || idx >= 2) return null;
    list[idx] = blankInstaller();
    return list[idx];
  }

  function renderInstallCompanies() {
    if (!installPanel) return;
    var tbody = installPanel.querySelector('.rq-ws-install-table__body');
    if (!tbody) return;
    /* ⚠️ `rows`, not `state.installation.companies`. When there are no installers the table still
       shows one empty line to type into — that line is this local object, and it reaches the
       record only through ensureInstallRow, on a real edit. */
    var rows = state.installation.companies.length
      ? state.installation.companies
      : [blankInstaller()];

    var html = '';
    rows.forEach(function(c, ci) {
      var typeClass = c.type === 'official' ? 'official' : c.type === 'estimate' ? 'estimate' : 'unset';
      if (!c.type) c.type = 'unset';
      var typeLabel = c.type === 'official' ? '✓' : c.type === 'estimate' ? 'E' : '?';
      var typeTitle = c.type === 'official' ? 'Official quote (click to toggle)' : c.type === 'estimate' ? 'Estimate (click to toggle)' : 'Not set — click to mark as Estimate or Official';

      html += '<tr data-install-idx="' + ci + '">';
      html += '<td style="position:relative;"><input type="text" value="' + escAttr(c.name || '') + '" placeholder="Company name..." data-install-idx="' + ci + '" data-install-field="name" style="font-weight:' + (c.name ? '700' : '400') + ';"></td>';
      html += '<td><div class="rq-ws-cost-cell">';
      html += '<input type="text" value="' + (c.cost ? fmt(c.cost) : '') + '" placeholder="$0.00" data-install-idx="' + ci + '" data-install-field="cost" class="rq-ws-cost--' + typeClass + '">';
      html += '<button type="button" class="rq-ws-type-toggle rq-ws-type-toggle--' + typeClass + '" data-install-idx="' + ci + '" title="' + typeTitle + '">' + typeLabel + '</button>';
      html += '</div></td>';
      html += '<td><input type="text" value="' + (c.markup != null ? c.markup : 10) + '" data-install-idx="' + ci + '" data-install-field="markup"></td>';
      html += '<td><input type="text" value="' + fmt(c.markedUpTotal) + '" data-install-idx="' + ci + '" data-install-field="charged"></td>';
      html += '<td><input type="text" value="' + escAttr(c.notes || '') + '" placeholder="Notes..." data-install-idx="' + ci + '" data-install-field="notes"></td>';
      html += '<td><button type="button" class="rq-ws-install-table__remove" data-install-idx="' + ci + '" title="Remove installer">&times;</button></td>';
      html += '</tr>';
    });

    tbody.innerHTML = html;
    recalcInstallTotals();
    bindInstallEvents(tbody);
  }

  function recalcInstallTotals() {
    var totalCost = 0, totalQuoted = 0;
    state.installation.companies.forEach(function(c) {
      if (!c._chargedOverride) {
        var mk = c.markup || 10;
        c.markedUpTotal = Math.round((c.cost || 0) * (1 + mk / 100) * 100) / 100;
      }
      totalCost += c.cost || 0;
      totalQuoted += c.markedUpTotal;
    });
    state.installation.totalCost = Math.round(totalCost * 100) / 100;
    state.installation.totalQuoted = Math.round(totalQuoted * 100) / 100;

    if (installPanel) {
      var costEl = installPanel.querySelector('.rq-ws-install-panel__cost-val');
      var quotedEl = installPanel.querySelector('.rq-ws-install-panel__quoted-val');
      if (costEl) costEl.textContent = fmt(state.installation.totalCost);
      if (quotedEl) quotedEl.textContent = fmt(state.installation.totalQuoted);
      var costEl2 = installPanel.querySelector('.rq-ws-install-panel__cost-val2');
      var quotedEl2 = installPanel.querySelector('.rq-ws-install-panel__quoted-val2');
      if (costEl2) costEl2.textContent = fmt(state.installation.totalCost);
      if (quotedEl2) quotedEl2.textContent = fmt(state.installation.totalQuoted);
    }
  }

  function bindInstallEvents(container) {
    container.querySelectorAll('[data-install-field]').forEach(function(el) {
      var evtType = el.tagName === 'SELECT' ? 'change' : 'input';
      el.addEventListener(evtType, function() {
        var idx = parseInt(this.getAttribute('data-install-idx'));
        var field = this.getAttribute('data-install-field');
        /* ⛔ ensureInstallRow, NOT a bare lookup. Typing into the placeholder line is the moment
           an installer becomes real, so this is where the row joins `companies` — and it is the
           ONLY editing path that commits one. */
        var c = ensureInstallRow(idx);
        if (!c) return;

        if (field === 'cost') {
          c.cost = parseMoney(this.value);
          c._chargedOverride = false;
          Q.liveFmtMoney(this);
        } else if (field === 'markup') {
          c.markup = Math.round(parseFloat(this.value) * 10000) / 10000 || 0;
          c._chargedOverride = false;
        } else if (field === 'charged') {
          var charged = parseMoney(this.value);
          if (c.cost > 0) {
            c.markup = Math.round(((charged / c.cost) - 1) * 100 * 10000) / 10000;
          } else {
            c.markup = 0;
          }
          c.markedUpTotal = charged;
          c._chargedOverride = true;
          var mkInput = container.querySelector('[data-install-idx="' + idx + '"][data-install-field="markup"]');
          if (mkInput) mkInput.value = c.markup;
          state.dirty = true;
          recalcInstallTotals();
          return;
        } else if (field === 'name') {
          c.name = this.value;
          showAutocomplete(this, 'installer', function(val) { c.name = val; state.dirty = true; });
        } else {
          c[field] = this.value;
        }
        state.dirty = true;
        recalcInstallTotals();
        var chargedInput = container.querySelector('[data-install-idx="' + idx + '"][data-install-field="charged"]');
        if (chargedInput && field !== 'charged') chargedInput.value = fmt(c.markedUpTotal);
      });

      if (el.getAttribute('data-install-field') === 'cost' || el.getAttribute('data-install-field') === 'charged') {
        el.addEventListener('focusout', function() {
          var n = parseMoney(this.value);
          this.value = n ? fmt(n) : '';
        });
      }
      if (el.getAttribute('data-install-field') === 'name') {
        el.addEventListener('focus', function() {
          var idx = parseInt(this.getAttribute('data-install-idx'));
          var self = this;
          /* ⛔ THE ROW IS RESOLVED INSIDE THE CALLBACK, NOT HERE. Merely tabbing through the name
             box must not create an installer — only PICKING a suggestion does, and that fires the
             callback. Resolving `c` up front would commit a row on focus and reintroduce the
             phantom this whole change removes, through a quieter door. */
          showAutocomplete(self, 'installer', function(val) {
            var c = ensureInstallRow(idx);
            if (!c) return;
            c.name = val; self.value = val; self.style.fontWeight = '700'; state.dirty = true;
          });
        });
        el.addEventListener('input', function() {
          this.style.fontWeight = this.value ? '700' : '400';
        });
      }
      if (el.getAttribute('data-install-field') === 'name') {
        el.addEventListener('blur', function() {
          var self = this;
          setTimeout(function() {
            addAutoHistory('installer', self.value);
            hideAutocomplete(self);
          }, 150);
        });
      }
    });

    container.querySelectorAll('.rq-ws-type-toggle').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var idx = parseInt(this.getAttribute('data-install-idx'));
        /* Marking a line Estimate or Official is real data, so it commits the placeholder too. */
        var c = ensureInstallRow(idx);
        if (!c) return;
        /* CUSTOM_RFQPORTAL_BY_CLAUDE — Three-state cycle (mirrors freight). */
        var nextI = { unset: 'estimate', estimate: 'official', official: 'unset' };
        c.type = nextI[c.type] || 'estimate';
        state.dirty = true;
        this.className = 'rq-ws-type-toggle rq-ws-type-toggle--' + c.type;
        this.textContent = c.type === 'official' ? '✓' : c.type === 'estimate' ? 'E' : '?';
        this.title = c.type === 'official'
          ? 'Official quote (click to toggle)'
          : c.type === 'estimate'
            ? 'Estimate (click to toggle)'
            : 'Not set — click to mark as Estimate or Official';
        /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
        var costInput = container.querySelector('[data-install-idx="' + idx + '"][data-install-field="cost"]');
        if (costInput) {
          costInput.classList.remove('rq-ws-cost--unset', 'rq-ws-cost--estimate', 'rq-ws-cost--official');
          costInput.classList.add('rq-ws-cost--' + c.type);
        }
        syncInstallApplyBtn();
      });
    });

    container.querySelectorAll('.rq-ws-install-table__remove').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var idx = parseInt(this.getAttribute('data-install-idx'));
        state.installation.companies.splice(idx, 1);
        state.dirty = true;
        renderInstallCompanies();
      });
    });
    syncInstallApplyBtn();
  }

  /* Add installer button */
  var installAddBtn = installPanel ? installPanel.querySelector('.rq-ws-install-panel__add') : null;
  if (installAddBtn) {
    installAddBtn.addEventListener('click', function() {
      if (state.installation.companies.length >= 2) return;
      /* The one deliberate commit that is NOT an edit — somebody pressed the button, which is the
         whole difference between this row and the placeholder the renderer draws. */
      state.installation.companies.push(blankInstaller());
      state.dirty = true;
      renderInstallCompanies();
    });
  }

  /* Apply installation to quote */
  var installApply = installPanel ? installPanel.querySelector('.rq-ws-install-panel__apply') : null;
  function syncInstallApplyBtn() {
    if (!installApply) return;
    var hasUnset = state.installation.companies.some(function(c) { return c.type === 'unset'; });
    /* ⛔ AND DISABLED WHEN THERE ARE NO INSTALLERS AT ALL (2026-09-03). `companies` could never be
       empty while the renderer seeded a row, so `.some()` on an empty array — false — used to be
       unreachable. Now that the placeholder stays out of state it is reachable, and without this
       clause Apply would light up on a deal with no installer and write `status:'quoted'` with a
       $0 installation fee onto the quote. A latent bug the fix above exposed rather than caused. */
    installApply.disabled = hasUnset || !state.installation.companies.length;
  }
  if (installApply) {
    installApply.addEventListener('click', function() {
      if (!state.installation.companies.length) {
        showToast('Add an installer before applying installation to the quote', 'error');
        return;
      }
      var unsetCompanies = state.installation.companies.filter(function(c) { return c.type === 'unset'; });
      if (unsetCompanies.length) {
        showToast('Set each installer to Estimate (E) or Official (✓) before applying', 'error');
        return;
      }
      recalcInstallTotals();
      if (state.installation.companies.length > 0) {
        state.installation.history.push({
          version: state.installation.history.length + 1,
          companies: JSON.parse(JSON.stringify(state.installation.companies)),
          totalCost: state.installation.totalCost,
          totalQuoted: state.installation.totalQuoted,
          date: new Date().toISOString()
        });
      }
      state.fees[1].amount = state.installation.totalQuoted;
      state.fees[1].status = 'value';
      state.installation.status = 'quoted';
      state.installation.appliedAt = new Date().toISOString();
      _stampAppliedCompanies();
      var instInput = wrap.querySelector('.rq-ws-fees__input[data-fee="installation"]');
      var instSel = wrap.querySelector('.rq-ws-fees__status[data-fee="installation"]');
      if (instInput) { instInput.value = fmt(state.installation.totalQuoted); instInput.style.display = ''; }
      if (instSel) { instSel.value = 'value'; syncCustomSelect(instSel); }
      state.dirty = true;
      Q.recalcTotals();
      var btn = this;
      var origText = btn.textContent;
      btn.textContent = '✓ Applied!';
      btn.style.background = 'var(--rq-success, #16a34a)';
      btn.style.color = '#fff';
      btn.style.pointerEvents = 'none';
      setTimeout(function() { btn.textContent = origText; btn.style.background = ''; btn.style.color = ''; btn.style.pointerEvents = ''; }, 2000);
    });
  }

  /* ── Delivery Conditions (highlight in Fees & Extras) ──────────────────
     Conditions are captured at request time inside the Request Freight
     dropdown (see workstation-actions.js). Default is no boxes checked
     (curbside / liftgate-required) — silent in the UI; ~90% of quotes.
     When ANY box is checked, paint a colored callout above the Freight row
     so admins (and reps) can't miss the exception when pulling carrier
     quotes. The state still cascades to per-vendor freight flags, so
     admin's per-vendor toggles stay in sync. */
  var deliveryHighlight = wrap.querySelector('[data-delivery-highlight]');
  function renderDeliveryHighlight() {
    if (!deliveryHighlight) return;
    var dc = (state.freight && state.freight.deliveryConditions) || {};
    var f = pickDeliveryFlags(dc);
    var _ftl = ftlRows(); // CUSTOM_FTLFLAG_BY_CLAUDE
    var anyChecked = f.loadingDock || f.forkLift || f.insideDelivery || _ftl.length > 0;
    if (!anyChecked) {
      deliveryHighlight.style.display = 'none';
      return;
    }
    var parts = [];
    /* CUSTOM_FTLFLAG_BY_CLAUDE (Ryan 2026-10-22) - a truckload says so first, and with no dock or forklift ticked, that the order waits. */
    if (_ftl.length) {
      parts.push(allFtl() ? 'Ships by FTL.' : 'FTL: ' + _ftl.map(function(v) { return v.name; }).join(', ') + '.'); // Ryan 2026-10-22 wording
      if (!f.loadingDock && !f.forkLift) parts.push('PO held until rep confirms customer is ready to unload.');
    }
    if (f.loadingDock && f.forkLift) {
      parts.push('Customer has both a loading dock and a forklift — liftgate not required.');
    } else if (f.loadingDock) {
      parts.push('Customer has a loading dock — liftgate not required.');
    } else if (f.forkLift) {
      parts.push('Customer has a forklift — liftgate not required.');
    }
    if (f.insideDelivery) parts.push('Inside delivery required.');
    var textEl = deliveryHighlight.querySelector('[data-delivery-highlight-text]');
    if (textEl) textEl.textContent = parts.join(' ');
    deliveryHighlight.style.display = '';
  }
  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-26) - installation SCOPE, the admin-side twin of the
     freight delivery flags directly below.

     ⛔ THE SINGLE WRITER. Both surfaces call this: the admin flags on the Installation Details card
     and the rep sub-options in the Request Quote dropdown. It repaints the admin card itself, and
     the dropdown repaints from state whenever it opens - the same arrangement setDeliveryFlag and
     syncDeliverySubChecked already have, rather than a second writer that could disagree.

     Mutual exclusion is structural: scope is ONE string, so setting 'flooring' leaves nothing to
     un-set on the equipment box - it simply repaints unchecked from the same value. */
  function setInstallScope(value) {
    if (!state.installation) state.installation = {};
    var next = (value === 'flooring' || value === 'equipment') ? value : '';
    state.installation.scope = next;
    state.dirty = true;
    renderInstallScopeFlags();
  }
  function renderInstallScopeFlags() {
    if (!installPanel) return;
    var scope = (state.installation && state.installation.scope) || '';
    installPanel.querySelectorAll('[data-install-scope-admin]').forEach(function(cb) {
      cb.checked = cb.getAttribute('data-install-scope-admin') === scope;
    });
  }
  if (installPanel) {
    installPanel.querySelectorAll('[data-install-scope-admin]').forEach(function(cb) {
      cb.addEventListener('change', function() {
        setInstallScope(this.checked ? this.getAttribute('data-install-scope-admin') : '');
      });
    });
    renderInstallScopeFlags();
  }

  function setDeliveryFlag(flag, value) {
    if (!state.freight.deliveryConditions) {
      state.freight.deliveryConditions = { insideDelivery: false, loadingDock: false, forkLift: false, freightInsurance: true, defaultDelivery: true };
    }
    state.freight.deliveryConditions[flag] = !!value;
    /* CUSTOM_FTLFLAG_BY_CLAUDE (Ryan 2026-10-22) - the whole-quote truckload box (the rep's dropdown, or the footer box on a
       one-supplier quote) sets every supplier row, and a truckload has no inside delivery. */
    if (flag === 'ftl') {
      (state.freight.vendors || []).forEach(function(v) { if (v && v.name) v.ftl = !!value; });
      if (value) state.freight.deliveryConditions.insideDelivery = false;
      syncCurbsideToFtl(); // Ryan 2026-10-22
    }
    if (flag === 'insideDelivery' && value && allFtl()) state.freight.deliveryConditions.insideDelivery = false;
    state.dirty = true;
    renderDeliveryHighlight();
    renderFreightDeliveryFlags();
    if (flag === 'ftl' && typeof recalcFreightTotals === 'function') recalcFreightTotals(); // CUSTOM_FTLCAPTWIN_BY_CLAUDE - the cap moves before the rows are drawn
    if (flag === 'ftl' && typeof renderFreightVendors === 'function') renderFreightVendors();
  }
  // Paint the admin-side order-level checkboxes from state. Called on
  // initial mount, on quote load, and after rep-side toggles via
  // setDeliveryFlag (so the admin's view stays in sync if both panels
  // happen to be visible at the same time).
  function renderFreightDeliveryFlags() {
    if (!freightPanel) return;
    var dc = (state.freight && state.freight.deliveryConditions) || {};
    freightPanel.querySelectorAll('[data-freight-delivery-flag]').forEach(function(cb) {
      var flag = cb.getAttribute('data-freight-delivery-flag');
      cb.checked = !!dc[flag];
      /* CUSTOM_FTLFLAG_BY_CLAUDE - the footer truckload box is the one-supplier box; inside delivery cannot ride a truckload. */
      if (flag === 'ftl') {
        cb.checked = allFtl();
        var lab = cb.closest('label');
        if (lab) lab.style.display = shippingRows().length > 1 ? 'none' : '';
      }
      if (flag === 'insideDelivery') {
        cb.disabled = allFtl();
        cb.title = allFtl() ? 'A full truckload has no inside delivery.' : '';
      }
    });
  }
  if (freightPanel) {
    freightPanel.querySelectorAll('[data-freight-delivery-flag]').forEach(function(cb) {
      cb.addEventListener('change', function() {
        var flag = this.getAttribute('data-freight-delivery-flag');
        setDeliveryFlag(flag, this.checked);
      });
    });
    renderFreightDeliveryFlags();
  }
  // Initial paint — hidden by default unless a loaded quote already has
  // exception flags set.
  renderDeliveryHighlight();

  // Exports
  Q.freightPanel = freightPanel;
  Q.installPanel = installPanel;
  Q.CARRIER_PRESETS = CARRIER_PRESETS;
  Q.getUniqueVendors = getUniqueVendors;
  Q.syncFreightVendors = syncFreightVendors;
  Q.recalcFreightTotals = recalcFreightTotals;
  Q.renderFreightVendors = renderFreightVendors;
  Q.renderInstallCompanies = renderInstallCompanies;
  Q.setInstallScope = setInstallScope;
  Q.renderInstallScopeFlags = renderInstallScopeFlags;
  Q.checkFreightStaleness = checkFreightStaleness;
  Q.buildItemsSnapshot = buildItemsSnapshot;
  Q.distributeFreightTarget = distributeFreightTarget;
  Q.fetchEmailSentLog = fetchEmailSentLog;
  Q.renderDeliveryHighlight = renderDeliveryHighlight;
  Q.setDeliveryFlag = setDeliveryFlag;
  Q.renderFreightDeliveryFlags = renderFreightDeliveryFlags;
})();
